import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  FormControlLabel,
  Checkbox,
  Divider,
  Paper,
  InputAdornment,
  IconButton,
  Collapse,
  Radio,
  RadioGroup,
  FormLabel,
  Tooltip,
  CardActionArea,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Autocomplete,
  TablePagination,
} from '@mui/material';
import { Iconify } from '../../components/iconify';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import Header from '../../../../Header';
import Footer from '../../../../Footer';
import PromotionBreadcrumb from './PromotionBreadcrumb';
import BuyXGetYFree from './BuyXGetYFree';
import BuyXGetYDiscount from './BuyXGetYDiscount';
import BuyXDiscount from './BuyXDiscount';
import {
  findPromotionProductListIndex,
  shiftProductErrorsAfterRemove,
  clearProductErrorsAtIndex,
  clearProductErrorKeys,
} from './promotionProductErrors';
import MultiLayerOffer from './MultiLayerOffer';
import {
  applySlabGetQuantityCompulsoryErrors,
  buildSlabGetQuantityCompulsoryErrors,
  toApiPromotionPricingType,
} from './promotionPricingHelpers';
import {
  fetchProductsList,
  fetchCategories,
  fetchBrands,
  fetchPromotionChannels,
  createPromotion,
} from '../../../../../services/PromotionServices';
import { toast } from 'sonner';
import '../../../../common/commonStyles.css';
import GroupOffer from './GroupOffer';
import { getProductSelectPopupStock } from './promotionStockHelpers';



const promotionTypes = [
  {
    id: 'buy-x-get-y-free',
    title: 'Buy X Get Y Free',
    description: 'Buy a specified quantity of items and get items free from another product group.',
  },
  {
    id: 'buy-x-get-y-discount',
    title: 'Buy X Get Y at Discount',
    description: 'Buy specified items and get another product at discounted price or set value.',
  },
  {
    id: 'buy-x-discount',
    title: 'Buy X at Discount...',
    description: 'Buy specified items and get discount on their total value.',
  },
  {
    id: 'multi-layer-offer',
    title: 'Multi-Layer Special Offer',
    description: 'Setup multiple slab-based offers with escalating discounts for same products.',
  },
  {
    id: 'group-offer',
    title: 'Group Offer',
    description: 'Setup multiple slab-based offers with escalating discounts for same products.',
  },
];

const parseNumber = (value) => {
  if (value === undefined || value === null || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const parseInteger = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.trunc(num) : 0;
};

/** Single user-facing string for create-promotion failures (avoids repeating message + errors.title). */
const formatCreatePromotionErrorResponse = (response) => {
  let errorMessage = response?.message || 'Failed to create promotion';
  if (typeof errorMessage === 'string') {
    errorMessage = errorMessage.replace(/^Validation error:\s*/i, '');
  }
  const errors = response?.errors && typeof response.errors === 'object' ? response.errors : {};
  const entries = Object.entries(errors);
  if (entries.length === 0) {
    return errorMessage;
  }
  const errorDetails = entries
    .map(([, message]) => {
      if (typeof message === 'string') {
        return message.replace(/^Validation error:\s*/i, '');
      }
      return String(message ?? '');
    })
    .filter(Boolean)
    .join(', ');
  const base = String(errorMessage || '').trim();
  const details = String(errorDetails || '').trim();
  if (!details) {
    return base;
  }
  if (!base) {
    return details;
  }
  const baseL = base.toLowerCase();
  const detailsL = details.toLowerCase();
  if (baseL === detailsL || baseL.includes(detailsL)) {
    return base;
  }
  return `${base}: ${details}`;
};

// Price helpers used by product filtering and display
// Prefer the first positive (non-zero) numeric value among candidates
function pickPositive(...vals) {
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  for (const v of vals) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

// Normalise price lookup across inconsistent API fields
function getForcedPrice(product, type = 'Box', mode = 'sales') {
  if (type === 'Piece') {
    if (mode === 'offer') {
      return parseNumber(pickPositive(product.pieceOfferPrice, product.offerPiecePrice, product.offerPrice, 0));
    }
    return parseNumber(
      pickPositive(
        product.pieceSalesPrice,
        product.PiecePrice,
        product.piece_sales_price,
        product.salesPrice,
        product.price,
        0
      )
    );
  }
  // Box
  if (mode === 'offer') {
    return parseNumber(pickPositive(product.boxOfferPrice, product.offerBoxPrice, product.offerPrice, 0));
  }
  return parseNumber(pickPositive(product.boxSalesPrice, product.salesPrice, product.box_sales_price, product.price, 0));
}

function getMultiLayerBuyPricingMode(product, priceKind = 'Box', defaultPricingType = 'sales') {
  if (priceKind === 'Piece') {
    const pieceMode = product.piecePricingType ?? product.piece_pricing_type;
    if (pieceMode && pieceMode !== '' && pieceMode !== 'undefined' && pieceMode !== 'null') {
      return pieceMode;
    }
  } else {
    const boxMode = product.boxPricingType ?? product.box_pricing_type;
    if (boxMode && boxMode !== '' && boxMode !== 'undefined' && boxMode !== 'null') {
      return boxMode;
    }
  }
  const fallback = product.pricingType ?? product.pricing_type;
  if (fallback && fallback !== '' && fallback !== 'undefined' && fallback !== 'null') {
    return fallback;
  }
  const resolvedDefault =
    defaultPricingType &&
    defaultPricingType !== '' &&
    defaultPricingType !== 'undefined' &&
    defaultPricingType !== 'null'
      ? defaultPricingType
      : 'sales';
  return resolvedDefault === 'offer' ? 'offer' : 'sales';
}

function getMinMultiLayerBuyPrice(products, priceKind = 'Box', defaultPricingType = 'sales') {
  if (!Array.isArray(products) || products.length === 0) return 0;
  const prices = products
    .map((product) =>
      getForcedPrice(
        product,
        priceKind,
        getMultiLayerBuyPricingMode(product, priceKind, defaultPricingType)
      )
    )
    .filter((price) => Number.isFinite(price) && price > 0);
  if (prices.length === 0) return 0;
  return Math.min(...prices);
}

function getCatalogProductPriceKind(product) {
  if (product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null') {
    return product.type === 'Piece' ? 'Piece' : 'Box';
  }
  return product.split === 1 ? 'Piece' : 'Box';
}

const ProductSelectionModal = ({
  open,
  onClose,
  onConfirm,
  title,
  selectedProducts = [],
  productTypeFilter = null,
  showToast,
  alreadyAddedProducts = [],
  firstBuyProduct = null,
  isGetForSlab = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState(['All']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [currentFirstProduct, setCurrentFirstProduct] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedItems(selectedProducts);
      setSearchQuery('');
      setDebouncedSearchQuery('');
      setSelectedCategory(null);
      setSelectedBrand('All');
      setCurrentPage(1);
      // Initialize currentFirstProduct from firstBuyProduct (from parent) or from selectedProducts
      if (firstBuyProduct) {
        console.log('MultiLayerOffer: Modal opened with firstBuyProduct from parent:', firstBuyProduct);
        setCurrentFirstProduct(firstBuyProduct);
      } else if (selectedProducts && selectedProducts.length > 0 && productTypeFilter) {
        console.log('MultiLayerOffer: Modal opened with selectedProducts, using first product:', selectedProducts[0]);
        setCurrentFirstProduct(selectedProducts[0]);
      } else {
        setCurrentFirstProduct(null);
      }
      loadCategories();
      loadBrands();
    }
  }, [open, selectedProducts, firstBuyProduct, productTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Watch for when first product is selected/changed in modal (for MultiLayerOffer)
  // Skip this for get products (getForSlab) - no need to update currentFirstProduct or reload API
  useEffect(() => {
    if (open && productTypeFilter && !isGetForSlab) {
      if (selectedItems.length === 0) {
        // No products selected in modal - reset currentFirstProduct (but keep firstBuyProduct from parent)
        if (currentFirstProduct && !firstBuyProduct) {
          setCurrentFirstProduct(null);
          setCurrentPage(1);
        }
      } else if (selectedItems.length >= 1) {
        // First product selected - update currentFirstProduct and reload API
        const firstSelected = selectedItems[0];
        const currentProductId = currentFirstProduct ? (currentFirstProduct.product_id || currentFirstProduct.id) : null;
        const newProductId = firstSelected.product_id || firstSelected.id;

        // Update if it's a different product (different ID) or if currentFirstProduct is null
        if (!currentFirstProduct || currentProductId !== newProductId) {
          console.log('MultiLayerOffer: Updating currentFirstProduct from selectedItems:', firstSelected);
          // Update first product (reloads with pack parameter) but keep the
          // user's current page so the list stays in place after selecting.
          setCurrentFirstProduct(firstSelected);
        }
      }
    }
  }, [selectedItems, open, productTypeFilter, currentFirstProduct, firstBuyProduct, isGetForSlab]);

  // Only include currentFirstProduct in dependency when not getForSlab
  const effectiveFirstProduct = useMemo(() => {
    return isGetForSlab ? null : currentFirstProduct;
  }, [isGetForSlab, currentFirstProduct]);

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open, currentPage, rowsPerPage, debouncedSearchQuery, selectedCategory, selectedBrand, firstBuyProduct, effectiveFirstProduct]);

  const loadCategories = async () => {
    try {
      const response = await fetchCategories();
      if (response.success && response.data && response.data.categories) {
        const categoryList = response.data.categories.map((cat) => ({
          id: cat.id,
          name: cat.name
        }));
        setCategories(categoryList);
      }
    } catch (err) {
    }
  };

  const loadBrands = async () => {
    try {
      const response = await fetchBrands();
      if (response.success && response.data && response.data.brands) {
        const brandList = response.data.brands.map((brand) => brand.name || brand);
        setBrands(['All', ...brandList]);
      }
    } catch (err) {
    }
  };

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        search: debouncedSearchQuery,
        category: selectedCategory && selectedCategory.id ? selectedCategory.id : '',
        brand: selectedBrand !== 'All' ? selectedBrand : '',
        page: currentPage,
        pageSize: rowsPerPage,
      };

      if (productTypeFilter && productTypeFilter !== null && productTypeFilter !== '') {
        const productTypeMap = {
          'box-only': 'box',
          'piece-only': 'piece',
          'both': 'both'
        };
        params.producttype = productTypeMap[productTypeFilter] || productTypeFilter;
      }

      // For get products (getForSlab): Pass box parameter only when productTypeFilter is box-only
      if (isGetForSlab && productTypeFilter === 'box-only') {
        params.box = '1';
        console.log('Get Products: Adding box parameter to API call for box-only product type');
      } else {
        // Add pack parameter when first product is selected (ONLY for Multi-Layer Offer promotion buy products)
        // Use currentFirstProduct (from modal selection) or firstBuyProduct (from parent) - whichever exists
        const firstProduct = currentFirstProduct || firstBuyProduct;
        if (firstProduct) {
          // Try multiple possible property names for pack size
          const packSize = parseInt(
            firstProduct.pack_size ||
            firstProduct.pack ||
            firstProduct.unit_per_box ||
            firstProduct.unitPerBox ||
            firstProduct.size ||
            0,
            10
          );
          console.log('MultiLayerOffer: First product pack size check:', {
            product: firstProduct,
            pack_size: firstProduct.pack_size,
            pack: firstProduct.pack,
            unit_per_box: firstProduct.unit_per_box,
            unitPerBox: firstProduct.unitPerBox,
            size: firstProduct.size,
            calculatedPackSize: packSize
          });
          if (packSize > 0) {
            params.pack = packSize;
            console.log('MultiLayerOffer: Adding pack parameter to API call:', packSize);
          } else {
            console.warn('MultiLayerOffer: Pack size is 0 or not found in product:', firstProduct);
          }
        } else {
          console.log('MultiLayerOffer: No first product available (currentFirstProduct:', currentFirstProduct, ', firstBuyProduct:', firstBuyProduct, ')');
        }
      }

      console.log('API call params:', params);
      const response = await fetchProductsList(params);

      if (response.success && response.data) {
        let filteredProducts = response.data.products || [];
        const newPagination = response.data.pagination || null;
        setProducts(filteredProducts);
        setPagination(newPagination);
        // Safety clamp: if a filter change (e.g. pack-size filter after selecting
        // the first product) shrinks the list below the current page, snap to the
        // last available page instead of showing an empty page.
        if (newPagination && newPagination.total_pages > 0 && currentPage > newPagination.total_pages) {
          setCurrentPage(newPagination.total_pages);
        }
      } else {
        setError('Failed to load products');
      }
    } catch (err) {
      setError('Error loading products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = pagination?.total_pages || 1;
  const startIndex = pagination ? (pagination.current_page - 1) * pagination.page_size + 1 : 0;
  const endIndex = pagination
    ? Math.min(startIndex + products.length - 1, pagination.total_count)
    : 0;
  const totalCount = pagination?.total_count || 0;

  const handleSelectProduct = (product) => {
    setSelectedItems((prev) => {
      const productIdentifier = product.product_id || product.id;
      const exists = prev.find((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
      if (exists) {
        return prev.filter((item) => {
          const itemIdentifier = item.product_id || item.id;
          return itemIdentifier !== productIdentifier;
        });
      } else {
        if (prev.length >= 200) {
          toast.warning(
            `You cannot add more than 200 products. Current count: ${prev.length}`
          );
          return prev;
        }

        // Skip validation for get products (getForSlab)
        if (!isGetForSlab) {
          const baseline = prev.length > 0 ? prev[0] : firstBuyProduct;
          if (baseline && productTypeFilter) {
            const productPackSize = parseInt(String(product.pack_size || product.pack || product.unit_per_box || 0), 10);
            const baselinePackSize = parseInt(String(baseline.pack_size || baseline.pack || baseline.unit_per_box || 0), 10);
            const epsilon = 0.005;
            let validationFailed = false;

            if (productTypeFilter === 'both') {
              // if (productPackSize !== baselinePackSize) {
              //   toast.error('Selected product must match the first product\'s pack size.');
              //   return prev;
              // }

              const productBoxPrice = getForcedPrice(product, 'Box', product.pricingType || 'sales');
              const baselineBoxPrice = getForcedPrice(baseline, 'Box', baseline.pricingType || 'sales');
              // if (Math.abs(productBoxPrice - baselineBoxPrice) >= epsilon) {
              //   toast.error('Selected product must match the first product\'s box price.');
              //   return prev;
              // }
            } else {
              // if (productPackSize !== baselinePackSize) {
              //   toast.error('Selected product must match the first product\'s pack size and price.');
              //   return prev;
              // }

              let priceMismatch = false;

              if (productTypeFilter === 'box-only') {
                const productBoxPrice = getForcedPrice(product, 'Box', product.pricingType || 'sales');
                const baselineBoxPrice = getForcedPrice(baseline, 'Box', baseline.pricingType || 'sales');
                if (Math.abs(productBoxPrice - baselineBoxPrice) >= epsilon) {
                  priceMismatch = true;
                }
              }

              if (productTypeFilter === 'piece-only') {
                const productPiecePrice = getForcedPrice(product, 'Piece', product.pricingType || 'sales');
                const baselinePiecePrice = getForcedPrice(baseline, 'Piece', baseline.pricingType || 'sales');
                if (Math.abs(productPiecePrice - baselinePiecePrice) >= epsilon) {
                  priceMismatch = true;
                }
              }

              if (priceMismatch) {
                // showToast('Selected product must match the first product\'s pack size and price.', 'error');
                // return prev;
              }
            }
          }
        }

        return [
          ...prev,
          {
            ...product,
            quantity: 1,
            compulsory: false,
            uniqueId: `${product.id}-${Date.now()}`,
          },
        ];
      }
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const baseline = selectedItems.length > 0 ? selectedItems[0] : firstBuyProduct;

      const newSelections = products
        .filter((product) => {
          const productIdentifier = product.product_id || product.id;
          const alreadySelected = selectedItems.some((item) => {
            const itemIdentifier = item.product_id || item.id;
            return itemIdentifier === productIdentifier;
          });
          const alreadyInOpposite = alreadyAddedProducts.some((item) => {
            const itemIdentifier = item.product_id || item.id;
            return itemIdentifier === productIdentifier;
          });

          if (alreadySelected || alreadyInOpposite) {
            return false;
          }

          // Skip validation for get products (getForSlab)
          if (!isGetForSlab && baseline && productTypeFilter) {
            const productPackSize = parseInt(String(product.pack_size || product.pack || product.unit_per_box || 0), 10);
            const baselinePackSize = parseInt(String(baseline.pack_size || baseline.pack || baseline.unit_per_box || 0), 10);

            if (productPackSize !== baselinePackSize) {
              return false;
            }

            const epsilon = 0.005;

            if (productTypeFilter === 'box-only' || productTypeFilter === 'both') {
              const productBoxPrice = getForcedPrice(product, 'Box', product.pricingType || 'sales');
              const baselineBoxPrice = getForcedPrice(baseline, 'Box', baseline.pricingType || 'sales');
              if (Math.abs(productBoxPrice - baselineBoxPrice) >= epsilon) {
                return false;
              }
            }

            if (productTypeFilter === 'piece-only' || productTypeFilter === 'both') {
              const productPiecePrice = getForcedPrice(product, 'Piece', product.pricingType || 'sales');
              const baselinePiecePrice = getForcedPrice(baseline, 'Piece', baseline.pricingType || 'sales');
              if (Math.abs(productPiecePrice - baselinePiecePrice) >= epsilon) {
                return false;
              }
            }
          }

          return true;
        })
        .map((product) => ({
          ...product,
          quantity: 1,
          compulsory: false,
          uniqueId: `${product.id}-${Date.now()}`,
        }));

      const totalAvailable = products.filter((product) => {
        const productIdentifier = product.product_id || product.id;
        const alreadySelected = selectedItems.some((item) => {
          const itemIdentifier = item.product_id || item.id;
          return itemIdentifier === productIdentifier;
        });
        const alreadyInOpposite = alreadyAddedProducts.some((item) => {
          const itemIdentifier = item.product_id || item.id;
          return itemIdentifier === productIdentifier;
        });
        return !alreadySelected && !alreadyInOpposite;
      }).length;

      const filteredCount = totalAvailable - newSelections.length;
      if (filteredCount > 0 && baseline && productTypeFilter) {
        toast.warning(
          `${filteredCount} product(s) were not selected due to pack size or price mismatch.`
        );
      }

      if (selectedItems.length + newSelections.length > 200) {
        toast.warning(
          `You cannot add more than 200 products. Current count: ${selectedItems.length}. Trying to add: ${newSelections.length}`
        );
        return;
      }

      setSelectedItems((prev) => [...prev, ...newSelections]);
    } else {
      const currentPageIds = products.map((p) => p.product_id || p.id);
      setSelectedItems((prev) => prev.filter((item) => {
        const itemIdentifier = item.product_id || item.id;
        return !currentPageIds.includes(itemIdentifier);
      }));
    }
  };

  const isAllSelected =
    products.length > 0 &&
    products.every((product) => {
      const productIdentifier = product.product_id || product.id;
      return selectedItems.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      }) || alreadyAddedProducts.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    });

  const isIndeterminate =
    products.some((product) => {
      const productIdentifier = product.product_id || product.id;
      return selectedItems.some((item) => {
        const itemIdentifier = item.product_id || item.id;
        return itemIdentifier === productIdentifier;
      });
    }) && !isAllSelected;

  const handleConfirm = () => {
    if (selectedItems.length === 0) {
      showToast('Please select at least one product', 'warning');
      return;
    }
    onConfirm(selectedItems);
    onClose();
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (event) => {
    setSelectedCategory(event.target.value);
    setCurrentPage(1);
  };

  const handleBrandChange = (event) => {
    setSelectedBrand(event.target.value);
    setCurrentPage(1);
  };

  const handleSearch = () => {
    setCurrentPage(1);
    loadProducts();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ my:-2}}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography className="page-title"
          >
            {title}
          </Typography>
          <Button onClick={onClose} sx={{ minWidth: 'auto' }}>
            <Iconify icon="eva:close-fill" />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: '80vh', overflowY: 'auto', mt: -1}}>
        <Box sx={{ p: 1.5, pb: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1, }}>
            <TextField
              className="input-border"
              fullWidth
              size="small"
              placeholder="Search by product name or product code"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-outline"/>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Autocomplete
              className="input-border"
              sx={{
                minWidth: 250,
              }}
              size="small"
              options={categories}
              value={selectedCategory}
              onChange={(event, newValue) => {
                handleCategoryChange({ target: { value: newValue || null } });
              }}
              getOptionLabel={(option) => option?.name || ''}
              renderInput={(params) => (
                <TextField {...params} className="input-border text-field" label="Category" placeholder="Select Category" />
              )}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />

            <Autocomplete
              className="input-border"
              sx={{
                minWidth: 250
              }}
              size="small"
              options={brands}
              value={selectedBrand}
              onChange={(event, newValue) => {
                handleBrandChange({ target: { value: newValue || 'All' } });
              }}
              renderInput={(params) => (
                <TextField
                {...params} className="input-border text-field" label="Brand" placeholder="Select Brand" />
              )}
              isOptionEqualToValue={(option, value) => option === value}
            />
          </Box>
        </Box>


        <TableContainer
        >
          <Table
            size="small"
            sx={{
              tableLayout: "auto",
            }}
            className="table-cell-border"
          >
            <TableHead>
              <TableRow className="table-cell-header">
                <TableCell padding="checkbox">
                  <Checkbox
                    className="checkBox"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    disabled={loading || products.length === 0}
                  />
                </TableCell>
                <TableCell width="70px">
                  Code
                </TableCell>
                <TableCell width="250px">
                  Name
                </TableCell>
                <TableCell width="70px">
                  Size
                </TableCell>
                <TableCell width="80px" align="right">
                  Cost Price
                </TableCell>
                <TableCell width="80px" align="right">
                  Sales Price
                </TableCell>
                <TableCell width="80px" align="right">
                  Offer Price
                </TableCell>
                <TableCell width="60px" align="right">
                  Discount
                </TableCell>
                <TableCell width="60px" align="right">
                  Stock
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 2,
                        fontSize: "0.75rem",
                        color: "#637381",
                      }}
                    >
                      Loading products...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8rem",
                      }}
                    >
                      No products found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => {
                  const productIdentifier = product.product_id || product.id;
                  const isSelected = selectedItems.some((item) => {
                    const itemIdentifier = item.product_id || item.id;
                    return itemIdentifier === productIdentifier;
                  });
                  const isAlreadyAdded = alreadyAddedProducts.some((item) => {
                    const itemIdentifier = item.product_id || item.id;
                    return itemIdentifier === productIdentifier;
                  });
                  const priceKind = getCatalogProductPriceKind(product);
                  return (
                    <TableRow
                      key={product.id}
                      hover
                      className="table-cell-style"
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          className="checkBox"
                          checked={isSelected}
                          onChange={() => handleSelectProduct(product)}
                          disabled={isAlreadyAdded}

                        />
                      </TableCell>
                      <TableCell >{product.code || product.product_code || product.id}</TableCell>
                      <TableCell >{product.name}</TableCell>
                      <TableCell >{product.size}</TableCell>
                      <TableCell align="right">£{(product.costPrice || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">£{getForcedPrice(product, priceKind, 'sales').toFixed(2)}</TableCell>
                      <TableCell align="right">£{getForcedPrice(product, priceKind, 'offer').toFixed(2)}</TableCell>
                      <TableCell align="right">{(product.margin || 0)}</TableCell>
                      <TableCell align="right">{getProductSelectPopupStock(product)}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <TablePagination
            className="pagination-styles"
            component="div"
            count={totalCount}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to}${count !== -1 ? ` of ${count}` : ''}`}

          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          className="button-variant-outlined"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          className="button-variant-contained"
          variant="contained"
        >
          Add Selected Products ({selectedItems.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function AddPromotion() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: null,
    endDate: null,
    selectedChannels: [],
    promotionType: 'buy-x-get-y-free',
    buyQuantity: '',
    getQuantity: '',
    buyProducts: [],
    getProducts: [],
    buyGroupQuantities: {},
    getGroupQuantities: {},
    discountValue: '',
    discountType: 'percentage',
    slabs: [],
    // Buy X at Discount Configuration defaults
    buyXDiscountType: 'no-discount',
    buyXDiscountValue: '',
    buyXDiscountApplyTo: 'none',
    buyDiscountConfigType: 'value-off',
    // Buy X Get Y at Discount Configuration defaults
    getDiscountType: 'set-value',
    getSetValue: '',
    getItemDiscountType: 'percentage',
    getItemDiscountValue: '',
    getItemDiscountApplyTo: 'bulk',
    buyDiscountType: 'percentage',
    buyDiscountValue: '',
    buyDiscountApplyTo: 'none',
    // Multi-Layer Offer Configuration defaults
    buyProductType: 'box-only',
    buyPricingType: 'sales',
  });

  const [errors, setErrors] = useState({});
  const [showBuyProducts, setShowBuyProducts] = useState(false);
  const [showGetProducts, setShowGetProducts] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [modalType, setModalType] = useState('buy');
  const [modalTitle, setModalTitle] = useState('');
  const [currentSlabIndex, setCurrentSlabIndex] = useState(null);
  const [multiLayerStep, setMultiLayerStep] = useState(1);
  const [channels, setChannels] = useState([]);
  const [websiteChannelValue, setWebsiteChannelValue] = useState(null);
  const [mobileAppChannelValue, setMobileAppChannelValue] = useState(null);
  const [buyProductsSearch, setBuyProductsSearch] = useState('');
  const [getProductsSearch, setGetProductsSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [showZeroPriceWarning, setShowZeroPriceWarning] = useState(false);
  const [zeroPriceProducts, setZeroPriceProducts] = useState([]);
  const [buyProducts, setBuyProducts] = useState([]);
  const [getProducts, setGetProducts] = useState([]);

  useEffect(() => {
    loadChannels();
  }, []);

  useEffect(() => {
    if (
      formData.promotionType === 'buy-x-get-y-discount' &&
      formData.buyDiscountApplyTo === 'bulk' &&
      formData.buyDiscountType === 'fixed' &&
      formData.buyDiscountValue &&
      formData.buyProducts.length > 0
    ) {
      const discountValue = parseFloat(formData.buyDiscountValue);
      if (!isNaN(discountValue) && discountValue >= 0) {
        const productsThatWouldBeFree = formData.buyProducts.filter((product) => {
          const productPrice = getProductUnitPrice(product);
          return productPrice > 0 && discountValue === productPrice;
        });
        if (productsThatWouldBeFree.length > 0) {
          const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
          setErrors((prev) => ({
            ...prev,
            buyDiscountValue: `Warning: Fixed discount (£${discountValue.toFixed(2)}) is equal to the price of some products (${productNames}), which may result in those products being free.`,
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors.buyDiscountValue && newErrors.buyDiscountValue.startsWith('Warning:')) {
              delete newErrors.buyDiscountValue;
            }
            return newErrors;
          });
        }
      }
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors.buyDiscountValue && newErrors.buyDiscountValue.startsWith('Warning:')) {
          delete newErrors.buyDiscountValue;
        }
        return newErrors;
      });
    }
  }, [formData.buyDiscountValue, formData.buyDiscountType, formData.buyDiscountApplyTo, formData.buyProducts, formData.promotionType]);

  useEffect(() => {
    if (
      formData.promotionType === 'buy-x-get-y-discount' &&
      formData.getDiscountType === 'value-off' &&
      formData.getItemDiscountApplyTo === 'bulk' &&
      formData.getItemDiscountType === 'fixed' &&
      formData.getItemDiscountValue &&
      formData.getProducts.length > 0
    ) {
      const discountValue = parseFloat(formData.getItemDiscountValue);
      if (!isNaN(discountValue) && discountValue >= 0) {
        const totalValue = getProductsBulkTotalValue(formData.getProducts);
        if (totalValue > 0 && discountValue < totalValue) {
          const productsThatWouldBeFree = formData.getProducts.filter((product) => {
            const productPrice = getProductUnitPrice(product);
            return productPrice > 0 && discountValue >= productPrice;
          });
          if (productsThatWouldBeFree.length > 0) {
            const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
            setErrors((prev) => ({
              ...prev,
              getItemDiscountValue: `Warning: Fixed discount (£${discountValue.toFixed(2)}) is greater than or equal to the price of some products (${productNames}), which may result in those products being free.`,
            }));
          } else {
            setErrors((prev) => {
              const newErrors = { ...prev };
              if (newErrors.getItemDiscountValue && newErrors.getItemDiscountValue.startsWith('Warning:')) {
                delete newErrors.getItemDiscountValue;
              }
              return newErrors;
            });
          }
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            if (newErrors.getItemDiscountValue && newErrors.getItemDiscountValue.startsWith('Warning:')) {
              delete newErrors.getItemDiscountValue;
            }
            return newErrors;
          });
        }
      }
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors.getItemDiscountValue && newErrors.getItemDiscountValue.startsWith('Warning:')) {
          delete newErrors.getItemDiscountValue;
        }
        return newErrors;
      });
    }
  }, [formData.getItemDiscountValue, formData.getItemDiscountType, formData.getItemDiscountApplyTo, formData.getDiscountType, formData.getProducts, formData.promotionType]);

  useEffect(() => {
    if (
      formData.promotionType !== 'buy-x-discount' ||
      formData.buyDiscountConfigType !== 'value-off' ||
      formData.buyXDiscountApplyTo !== 'per-item'
    ) {
      return;
    }

    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (/^buyProduct_\d+_discountValue$/.test(key)) {
          delete next[key];
        }
      });

      formData.buyProducts.forEach((product, index) => {
        if (product.buyXItemDiscountType !== 'fixed') {
          return;
        }
        const rawValue = product.buyXItemDiscountValue;
        if (rawValue === '' || rawValue === undefined || rawValue === null) {
          return;
        }
        const discountValue = parseFloat(rawValue);
        if (Number.isNaN(discountValue)) {
          return;
        }
        const productPrice = getProductUnitPrice(product);
        if (productPrice > 0 && discountValue > productPrice) {
          const productLabel =
            product.name || product.product_name || product.code || `Product ${index + 1}`;
          next[`buyProduct_${index}_discountValue`] =
            `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price (£${productPrice.toFixed(2)}). Invalid product: ${productLabel}`;
        }
      });

      return next;
    });
  }, [
    formData.promotionType,
    formData.buyDiscountConfigType,
    formData.buyXDiscountApplyTo,
    formData.buyProducts,
  ]);


  const loadChannels = async () => {
    try {
      const response = await fetchPromotionChannels();
      if (response.success && response.data && response.data.channels) {
        const websiteChannel = response.data.channels.find(
          (channel) => channel.is_active && channel.name === 'website'
        );
        if (websiteChannel) {
          setWebsiteChannelValue(websiteChannel.name);
        }

        const mobileAppChannel = response.data.channels.find(
          (channel) => channel.is_active && (channel.name === 'mobile-app' || channel.display_name === 'Mobile Application')
        );
        if (mobileAppChannel) {
          setMobileAppChannelValue(mobileAppChannel.name);
        }

        const channelList = response.data.channels
          .filter((channel) => channel.is_active && channel.name !== 'website')
          .map((channel) => ({
            id: channel.id,
            label:
              channel.name === 'mobile-app' || channel.display_name === 'Mobile Application'
                ? 'Customer'
                : channel.display_name,
            value: channel.name,
            isMobileApp: channel.name === 'mobile-app' || channel.display_name === 'Mobile Application',
          }));
        setChannels(channelList);
      }
    } catch (err) {
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'buyPricingType') {
      const pricingType = value === 'offer' ? 'offer' : 'sales';
      setFormData((prev) => ({
        ...prev,
        buyPricingType: pricingType,
        buyProducts: (prev.buyProducts || []).map((product) => ({
          ...product,
          pricingType,
          boxPricingType: pricingType,
          piecePricingType: pricingType,
        })),
      }));
    } else if (field === 'promotionType') {
      setFormData((prev) => ({
        ...prev,
        promotionType: value,
        ...(value === 'multi-layer-offer' && (prev.buyProducts || []).length === 0
          ? { buyProductType: 'box-only' }
          : {}),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    if (field === 'slabs') {
      setErrors((prev) => applySlabGetQuantityCompulsoryErrors(prev, value));
    }

    if (field === 'promotionType') {
      setMultiLayerStep(1);
    }

    // Clear stale validation as the user fixes fields (error keys do not always match field names).
    if (
      field === 'buyGroupQuantities' ||
      field === 'getGroupQuantities' ||
      field === 'buyQuantity' ||
      field === 'getQuantity'
    ) {
      const errorPrefix =
        field === 'buyGroupQuantities' || field === 'buyQuantity'
          ? 'buyGroupQty'
          : 'getGroupQty';
      setErrors((prev) => {
        let changed = false;
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key === errorPrefix || key.startsWith(`${errorPrefix}_`)) {
            if (next[key]) {
              delete next[key];
              changed = true;
            }
          }
        });
        if (field === 'buyQuantity') {
          if (next.buyQuantity) {
            delete next.buyQuantity;
            changed = true;
          }
          if (next.compulsoryQuantity) {
            delete next.compulsoryQuantity;
            changed = true;
          }
        }
        if (field === 'getQuantity' && next.getQuantity) {
          delete next.getQuantity;
          changed = true;
        }
        return changed ? next : prev;
      });
    } else if (field === 'buyProducts' || field === 'getProducts') {
      const prefix = field === 'buyProducts' ? 'buyProduct' : 'getProduct';
      setErrors((prev) => {
        let changed = false;
        const next = { ...prev };
        if (next[field]) {
          delete next[field];
          changed = true;
        }
        if (field === 'buyProducts' && next.compulsoryProducts) {
          delete next.compulsoryProducts;
          changed = true;
        }
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${prefix}_`)) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    } else if (errors[field] && !errors[field]?.startsWith('Warning:')) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleChannelToggle = (channelId) => {
    const channel = channels.find((ch) => ch.value === channelId);
    const isMobileApp = channel?.isMobileApp || false;

    setFormData((prev) => {
      if (isMobileApp) {
        const hasMobileApp = mobileAppChannelValue && prev.selectedChannels.includes(mobileAppChannelValue);
        if (hasMobileApp) {
          const newChannels = prev.selectedChannels.filter((c) => c !== mobileAppChannelValue);
          if (websiteChannelValue && newChannels.includes(websiteChannelValue)) {
            return {
              ...prev,
              selectedChannels: newChannels.filter((c) => c !== websiteChannelValue),
            };
          }
          return {
            ...prev,
            selectedChannels: newChannels,
          };
        } else {
          const newChannels = [...prev.selectedChannels];
          if (mobileAppChannelValue && !newChannels.includes(mobileAppChannelValue)) {
            newChannels.push(mobileAppChannelValue);
          }
          if (websiteChannelValue && !newChannels.includes(websiteChannelValue)) {
            newChannels.push(websiteChannelValue);
          }
          return {
            ...prev,
            selectedChannels: newChannels,
          };
        }
      } else {
        return {
          ...prev,
          selectedChannels: prev.selectedChannels.includes(channelId)
            ? prev.selectedChannels.filter((c) => c !== channelId)
            : [...prev.selectedChannels, channelId],
        };
      }
    });

    // Error key is `channels`, not `selectedChannels`.
    setErrors((prev) => {
      if (!prev.channels) return prev;
      const next = { ...prev };
      delete next.channels;
      return next;
    });
  };

  const handleAddProduct = (product, type) => {
    const productData = {
      ...product,
      quantity: 1,
      compulsory: type === 'buy',
    };

    const listKey = type === 'buy' ? 'buyProducts' : 'getProducts';
    const prefix = type === 'buy' ? 'buyProduct' : 'getProduct';
    const newIndex = formData[listKey].length;

    setFormData((prev) => ({
      ...prev,
      [listKey]: [...prev[listKey], productData],
    }));

    setErrors((prev) => clearProductErrorsAtIndex(prev, newIndex, prefix));
  };

  const handleOpenProductModal = (type) => {
    setModalType(type);
    setModalTitle(type === 'buy' ? 'Add Buy Products' : 'Add Get Products');
    setShowProductModal(true);
  };

  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setCurrentSlabIndex(null);
  };

  const getDefaultProductType = (split) => {
    if (split === 0) return 'Box';
    if (split === 1) return 'Piece';
    return 'Box';
  };

  const getDefaultTypeForMultiLayer = (product) => {
    if (formData.promotionType === 'multi-layer-offer') {
      const buyProductType = formData.buyProductType || 'box-only';

      // For get products (getForSlab)
      if (modalType === 'getForSlab') {
        if (buyProductType === 'box-only') return 'Box';
        if (buyProductType === 'piece-only') return 'Piece';
        if (buyProductType === 'both') return 'Box'; // Always default to Box for 'both' in get products
      }

      // For buy products
      if (modalType === 'buy') {
        if (buyProductType === 'box-only') return 'Box';
        if (buyProductType === 'piece-only') return 'Piece';
        if (buyProductType === 'both') {
          if (product.split === 0) return 'Box';
          if (product.split === 1) return 'Piece';
          if (product.split === 2) return 'Both';
        }
      }
    }
    const hasValidType = product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null';
    return hasValidType ? product.type : getDefaultProductType(product.split);
  };

  const getSelectedProductType = (product) => {
    const type = product.type;
    if (type && type !== '' && type !== 'undefined' && type !== 'null') {
      return type;
    }
    return getDefaultProductType(product.split);
  };

  const getPriceByType = (product, mode = 'sales') => {
    const selectedType = getSelectedProductType(product);
    return getForcedPrice(product, selectedType, mode);
  };

  const formatPrice = (value) => parseNumber(value).toFixed(2);

  const handleConfirmProducts = (selectedProducts) => {
    const buildProductsWithType = (prevFormData) => {
      const isMultiLayerBuy =
        prevFormData.promotionType === 'multi-layer-offer' && modalType === 'buy';
      const buyPricingType = prevFormData.buyPricingType === 'offer' ? 'offer' : 'sales';

      return selectedProducts.map((product, index) => {
        const productId = product.product_id || product.id;
        const productCode = product.product_code || product.code;
        const resolvedPricing = isMultiLayerBuy
          ? buyPricingType
          : product.pricingType || 'sales';

        return {
          ...product,
          id: productId,
          code: productCode || productId,
          type: getDefaultTypeForMultiLayer(product),
          pricingType: resolvedPricing,
          boxPricingType: isMultiLayerBuy
            ? buyPricingType
            : product.boxPricingType || product.pricingType || 'sales',
          piecePricingType: isMultiLayerBuy
            ? buyPricingType
            : product.piecePricingType || product.pricingType || 'sales',
          originalIndex: index,
          buyXItemDiscountType: 'no-discount',
          buyXItemDiscountValue: '',
          pack_size: product.pack_size || parseInt(product.pack || product.unit_per_box || 0),
          pack: product.pack || product.pack_size || product.unit_per_box,
        };
      });
    };

    if (modalType === 'getForSlab' && currentSlabIndex !== null) {
      setFormData((prev) => {
        const productsWithType = buildProductsWithType(prev);
        const newSlabs = [...(prev.slabs || [])];
        newSlabs[currentSlabIndex] = {
          ...newSlabs[currentSlabIndex],
          getProducts: productsWithType,
        };
        setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
        return {
          ...prev,
          slabs: newSlabs,
        };
      });
      setCurrentSlabIndex(null);
    } else {
      const prefix = modalType === 'buy' ? 'buyProduct' : 'getProduct';
      let updatedProducts = [];
      setFormData((prev) => {
        updatedProducts = buildProductsWithType(prev);
        return {
          ...prev,
          [modalType === 'buy' ? 'buyProducts' : 'getProducts']: updatedProducts,
        };
      });
      setErrors((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          if (key.startsWith(`${prefix}_`)) {
            delete next[key];
          }
        });
        return next;
      });

      if (formData.promotionType === 'multi-layer-offer' && modalType === 'buy' && updatedProducts.length > 1) {
        setTimeout(() => {
          // const packSizePriceErrors = {};
          const firstProduct = updatedProducts[0];
          if (firstProduct) {
            const firstPackSize = parseInt(String(firstProduct.pack_size || firstProduct.pack || firstProduct.unit_per_box || 0), 10);
            const firstBoxPrice = getForcedPrice(firstProduct, 'Box', getMultiLayerBuyPricingMode(firstProduct, 'Box'));
            const firstPiecePrice = getForcedPrice(firstProduct, 'Piece', getMultiLayerBuyPricingMode(firstProduct, 'Piece'));
            const epsilon = 0.005;
            const buyProductType = formData.buyProductType || 'box-only';

            updatedProducts.forEach((product, index) => {
              if (index > 0 && product) {
                const originalIndex = product.originalIndex !== undefined ? product.originalIndex : index;

                if (buyProductType === 'box-only' || buyProductType === 'both') {
                  const productBoxPrice = getForcedPrice(product, 'Box', getMultiLayerBuyPricingMode(product, 'Box'));
                  if (Math.abs(productBoxPrice - firstBoxPrice) >= epsilon) {
                    // packSizePriceErrors[`buyProduct_${originalIndex}_boxPrice`] = `Price or pack size mismatch.`;
                  }
                }

                if (buyProductType === 'piece-only' || buyProductType === 'both') {
                  const productPiecePrice = getForcedPrice(product, 'Piece', getMultiLayerBuyPricingMode(product, 'Piece'));
                  if (Math.abs(productPiecePrice - firstPiecePrice) >= epsilon) {
                    // packSizePriceErrors[`buyProduct_${originalIndex}_piecePrice`] = `Price or pack size mismatch.`;
                  }
                }
              }
            });

            setErrors((prev) => {
              const newErrors = { ...prev };
              Object.keys(newErrors).forEach(key => {
                if (key.startsWith('buyProduct_') && (key.includes('_packSize') || key.includes('_boxPrice') || key.includes('_piecePrice'))) {
                  delete newErrors[key];
                }
              });
              return { ...newErrors };
            });
          }
        }, 100);
      }
    }
  };

  useEffect(() => {
    if (formData.promotionType === 'multi-layer-offer' && formData.buyProducts.length > 1) {
      // const packSizePriceErrors = {};
      const firstProduct = formData.buyProducts[0];
      if (!firstProduct) return;

      const firstPackSize = parseInt(String(firstProduct.pack_size || firstProduct.pack || firstProduct.unit_per_box || 0), 10);
      const firstBoxPrice = getForcedPrice(firstProduct, 'Box', getMultiLayerBuyPricingMode(firstProduct, 'Box'));
      const firstPiecePrice = getForcedPrice(firstProduct, 'Piece', getMultiLayerBuyPricingMode(firstProduct, 'Piece'));
      const epsilon = 0.005;
      const buyProductType = formData.buyProductType || 'box-only';

      formData.buyProducts.forEach((product, index) => {
        if (product && index > 0) {
          const originalIndex = product.originalIndex !== undefined ? product.originalIndex : index;

          if (buyProductType === 'box-only' || buyProductType === 'both') {
            const productBoxPrice = getForcedPrice(product, 'Box', getMultiLayerBuyPricingMode(product, 'Box'));
            if (Math.abs(productBoxPrice - firstBoxPrice) >= epsilon) {
              // packSizePriceErrors[`buyProduct_${originalIndex}_boxPrice`] = `Price or pack size mismatch.`;
            }
          }

          if (buyProductType === 'piece-only' || buyProductType === 'both') {
            const productPiecePrice = getForcedPrice(product, 'Piece', getMultiLayerBuyPricingMode(product, 'Piece'));
            if (Math.abs(productPiecePrice - firstPiecePrice) >= epsilon) {
              // packSizePriceErrors[`buyProduct_${originalIndex}_piecePrice`] = `Price or pack size mismatch.`;
            }
          }
        }
      });

      setErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('buyProduct_') && (key.includes('_packSize') || key.includes('_boxPrice') || key.includes('_piecePrice'))) {
            delete newErrors[key];
          }
        });
        return { ...newErrors };
      });
    } else if (formData.promotionType === 'multi-layer-offer' && formData.buyProducts.length <= 1) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        Object.keys(newErrors).forEach(key => {
          if (key.startsWith('buyProduct_') && (key.includes('_packSize') || key.includes('_boxPrice') || key.includes('_piecePrice'))) {
            delete newErrors[key];
          }
        });
        return newErrors;
      });
    }
  }, [formData.buyProducts, formData.buyProductType, formData.promotionType]);

  useEffect(() => {
    if (formData.description && formData.description.length > 500) {
      setErrors((prev) => ({
        ...prev,
        description: 'Description cannot exceed 500 characters',
      }));
    } else if (errors.description === 'Description cannot exceed 500 characters') {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (formData.description.trim()) {
          delete newErrors.description;
        } else {
          newErrors.description = 'Description is required';
        }
        return newErrors;
      });
    }
  }, [formData.description]);

  const handleProductQuantityChange = (productId, type, quantity) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, quantity: quantity === '' ? '' : (parseInt(quantity) || 1) }
          : product
      ),
    }));
  };

  const handleProductCompulsoryChange = (productId, type, compulsory) => {
    setFormData((prev) => ({
      ...prev,
      [type === 'buy' ? 'buyProducts' : 'getProducts']: prev[
        type === 'buy' ? 'buyProducts' : 'getProducts'
      ].map((product, index) =>
        `${product.id}-${index}` === productId
          ? {
            ...product,
            compulsory,
            // Set default quantity to 1 when marking as compulsory
            quantity: compulsory && (!product.quantity || product.quantity === '') ? 1 : product.quantity
          }
          : product
      ),
    }));
  };

  const handleProductTypeChange = (productId, type, newType) => {
    const listKey = type === 'buy' ? 'buyProducts' : 'getProducts';
    const prefix = type === 'buy' ? 'buyProduct' : 'getProduct';
    const productIndex = findPromotionProductListIndex(formData[listKey], productId);

    setFormData((prev) => ({
      ...prev,
      [listKey]: prev[listKey].map((product, index) => {
        if (`${product.id}-${index}` === productId) {
          return { ...product, type: newType || getDefaultProductType(product.split) };
        }
        const currentType = product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null'
          ? product.type
          : getDefaultProductType(product.split);
        return { ...product, type: currentType };
      }),
    }));

    if (productIndex >= 0) {
      setErrors((prev) => clearProductErrorKeys(prev, productIndex, prefix, ['type']));
    }
  };

  const handleProductPricingChange = (productId, type, pricingType) => {
    const listKey = type === 'buy' ? 'buyProducts' : 'getProducts';
    const prefix = type === 'buy' ? 'buyProduct' : 'getProduct';
    const productIndex = findPromotionProductListIndex(formData[listKey], productId);

    setFormData((prev) => ({
      ...prev,
      [listKey]: prev[listKey].map((product, index) =>
        `${product.id}-${index}` === productId ? { ...product, pricingType } : product
      ),
    }));

    if (productIndex >= 0) {
      setErrors((prev) =>
        clearProductErrorKeys(prev, productIndex, prefix, ['pricing', 'offerPrice'])
      );
    }
  };

  const handleRemoveProduct = (productId, type) => {
    const listKey = type === 'buy' ? 'buyProducts' : 'getProducts';
    const prefix = type === 'buy' ? 'buyProduct' : 'getProduct';
    const removedIndex = findPromotionProductListIndex(formData[listKey], productId);

    setFormData((prev) => ({
      ...prev,
      [listKey]: prev[listKey].filter((p, index) => `${p.id}-${index}` !== productId),
    }));

    if (removedIndex >= 0) {
      setErrors((prev) => shiftProductErrorsAfterRemove(prev, removedIndex, prefix));
    }
  };

  const handleProductItemDiscountTypeChange = (productId, discountType) => {
    setFormData((prev) => ({
      ...prev,
      getProducts: prev.getProducts.map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, itemDiscountType: discountType }
          : product
      ),
    }));
  };

  const handleProductItemDiscountValueChange = (productId, discountValue) => {
    setFormData((prev) => ({
      ...prev,
      getProducts: prev.getProducts.map((product, index) =>
        `${product.id}-${index}` === productId
          ? { ...product, itemDiscountValue: discountValue }
          : product
      ),
    }));
  };

  const handleSlabProductQuantityChange = (slabIndex, productId, quantity) => {
    setFormData((prev) => {
      const newSlabs = [...(prev.slabs || [])];
      newSlabs[slabIndex] = {
        ...newSlabs[slabIndex],
        getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
          `${product.id}-${index}` === productId
            ? { ...product, quantity: quantity === '' ? '' : (parseInt(quantity) || 1) }
            : product
        ),
      };
      setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
      return { ...prev, slabs: newSlabs };
    });
  };

  const handleSlabProductTypeChange = (slabIndex, productId, newType) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
        `${product.id}-${index}` === productId ? { ...product, type: newType } : product
      ),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabProductPricingChange = (slabIndex, productId, field, value) => {
    const newSlabs = [...(formData.slabs || [])];
    newSlabs[slabIndex] = {
      ...newSlabs[slabIndex],
      getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) => {
        if (`${product.id}-${index}` !== productId) return product;
        const pt =
          product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null'
            ? product.type
            : getDefaultProductType(product.split);
        const next = { ...product, [field]: value };
        if (field !== 'pricingType') return next;
        if (pt === 'Piece') return { ...next, piecePricingType: value };
        if (pt === 'Box') return { ...next, boxPricingType: value };
        if (pt === 'Both') return { ...next, boxPricingType: value, piecePricingType: value };
        return next;
      }),
    };
    setFormData((prev) => ({ ...prev, slabs: newSlabs }));
  };

  const handleSlabProductCompulsoryChange = (slabIndex, productId, checked) => {
    setFormData((prev) => {
      const newSlabs = [...(prev.slabs || [])];
      newSlabs[slabIndex] = {
        ...newSlabs[slabIndex],
        getProducts: (newSlabs[slabIndex].getProducts || []).map((product, index) =>
          `${product.id}-${index}` === productId
            ? {
              ...product,
              compulsory: checked,
              quantity: checked && (!product.quantity || product.quantity === '') ? 1 : product.quantity
            }
            : product
        ),
      };
      setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
      return { ...prev, slabs: newSlabs };
    });
  };

  const handleSlabRemoveProduct = (slabIndex, productId) => {
    setFormData((prev) => {
      const newSlabs = [...(prev.slabs || [])];
      newSlabs[slabIndex] = {
        ...newSlabs[slabIndex],
        getProducts: (newSlabs[slabIndex].getProducts || []).filter(
          (p, index) => `${p.id}-${index}` !== productId
        ),
      };
      setErrors((prevErrors) => applySlabGetQuantityCompulsoryErrors(prevErrors, newSlabs));
      return { ...prev, slabs: newSlabs };
    });
  };

  const validateBuyXGetYFree = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one buy product';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `You cannot add more than 200 products. Current count: ${formData.buyProducts.length}`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Pricing Type is necessary';
      }
      applyZeroPriceProductError(errors, product, index, 'buyProduct');
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`buyProduct_${index}_quantity`] = 'Quantity is required for compulsory products';
      }
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity must be at least 1';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > buyQty) {
          errors.buyQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.getProducts.length === 0) {
      errors.getProducts = 'Please add at least one get product';
    } else if (formData.getProducts.length > 200) {
      errors.getProducts = 'You cannot add more than 200 products to get list';
    }

    formData.getProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`getProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`getProduct_${index}_pricing`] = 'Pricing Type is necessary';
      }
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`getProduct_${index}_quantity`] = 'Quantity is required for compulsory products';
      }
    });

    if (!formData.getQuantity || formData.getQuantity === '') {
      errors.getQuantity = 'Get quantity is required and must be a positive number';
    } else {
      const getQty = parseInt(formData.getQuantity);
      if (isNaN(getQty) || getQty < 1) {
        errors.getQuantity = 'Get quantity must be at least 1';
      } else if (getQty > 9999) {
        errors.getQuantity = 'Get quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.getProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > getQty) {
          errors.getQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
        }
      }
    }

    return errors;
  };

  const getNormalizedProductType = (product) => {
    if (product.type && product.type !== '' && product.type !== 'undefined' && product.type !== 'null') {
      return product.type;
    }
    if (product.split === 1) return 'Piece';
    return 'Box';
  };

  const getProductUnitPrice = (product) => {
    const type = getNormalizedProductType(product);
    const pricingMode =
      product.pricingType && product.pricingType !== '' && product.pricingType !== 'undefined' && product.pricingType !== 'null'
        ? product.pricingType
        : 'sales';

    if (type === 'Piece') {
      if (pricingMode === 'offer') {
        return parseNumber(pickPositive(product.pieceOfferPrice, product.offerPiecePrice, product.offerPrice, 0));
      }
      return parseNumber(
        pickPositive(
          product.pieceSalesPrice,
          product.PiecePrice,
          product.piece_sales_price,
          product.salesPrice,
          product.price,
          0
        )
      );
    }

    if (pricingMode === 'offer') {
      return parseNumber(pickPositive(product.boxOfferPrice, product.offerBoxPrice, product.offerPrice, 0));
    }
    return parseNumber(pickPositive(product.boxSalesPrice, product.salesPrice, product.box_sales_price, product.price, 0));
  };

  const getProductTotalValue = (product) => {
    const quantity = parseInt(product.quantity, 10);
    const normalizedQuantity = Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    return getProductUnitPrice(product) * normalizedQuantity;
  };

  const getProductsTotalValue = (products = []) =>
    products.reduce((sum, product) => sum + getProductTotalValue(product), 0);

  const getProductsBulkTotalValue = (products = []) =>
    products.reduce((sum, product) => sum + getProductUnitPrice(product), 0);

  const getProductErrorIndex = (product, index) =>
    product.originalIndex !== undefined ? product.originalIndex : index;

  const getProductDisplayLabel = (product) => {
    const name = product.name || product.product_name || 'Product';
    const code = product.code || product.product_code || product.id;
    return code ? `${name} (${code})` : name;
  };

  const applyZeroPriceProductError = (errors, product, index, keyPrefix = 'buyProduct') => {
    if (!product.pricingType) return;
    const productKey = getProductErrorIndex(product, index);
    const unitPrice = getProductUnitPrice(product);
    if (unitPrice !== 0) return;

    const pricingLabel = product.pricingType === 'offer' ? 'Offer' : 'Standard';
    const message = `${getProductDisplayLabel(product)}: ${pricingLabel} price is 0.00. Please select a valid price.`;
    const errorKey =
      product.pricingType === 'offer'
        ? `${keyPrefix}_${productKey}_offerPrice`
        : `${keyPrefix}_${productKey}_pricing`;
    errors[errorKey] = message;
  };

  const applyMultiLayerBuyZeroPriceErrors = (errors, product, index) => {
    const productKey = getProductErrorIndex(product, index);
    const buyProductType = formData.buyProductType || 'box-only';
    const buyPricingType = formData.buyPricingType || 'sales';
    const productType = product.type || 'Box';
    const label = getProductDisplayLabel(product);

    const setZeroError = (priceKind, errorKey, mode) => {
      const price = getForcedPrice(product, priceKind, mode);
      if (price === 0) {
        const pricingLabel = mode === 'offer' ? 'Offer' : 'Standard';
        errors[errorKey] = `${label}: ${priceKind} ${pricingLabel} price is 0.00. Please select a valid price.`;
      }
    };

    if ((buyProductType === 'box-only' || buyProductType === 'both') && (productType === 'Box' || productType === 'Both')) {
      const mode = getMultiLayerBuyPricingMode(product, 'Box', buyPricingType);
      setZeroError('Box', `buyProduct_${productKey}_boxPrice`, mode);
    }
    if ((buyProductType === 'piece-only' || buyProductType === 'both') && (productType === 'Piece' || productType === 'Both')) {
      const mode = getMultiLayerBuyPricingMode(product, 'Piece', buyPricingType);
      setZeroError('Piece', `buyProduct_${productKey}_piecePrice`, mode);
    }
  };

  const validateBuyXGetYDiscount = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one buy product';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `You cannot add more than 200 products. Current count: ${formData.buyProducts.length}`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
      applyZeroPriceProductError(errors, product, index, 'buyProduct');
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity must be at least 1';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > buyQty) {
          errors.buyQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
      if (formData.buyDiscountApplyTo === 'bulk') {
        if (!formData.buyDiscountValue || formData.buyDiscountValue === '') {
          errors.buyDiscountValue = 'Please enter a discount value';
        } else {
          const discountValue = parseFloat(formData.buyDiscountValue);
          if (isNaN(discountValue)) {
            errors.buyDiscountValue = 'Discount value must be a valid number';
          } else if (formData.buyDiscountType === 'percentage') {
            if (discountValue < 0 || discountValue > 100) {
              errors.buyDiscountValue = 'Discount percentage must be between 0 and 100';
            }
          } else if (formData.buyDiscountType === 'fixed') {
            if (discountValue < 0) {
              errors.buyDiscountValue = 'Fixed discount cannot be negative';
            } else {
              const productsWithInvalidDiscount = formData.buyProducts.filter((product) => {
                const productPrice = getProductUnitPrice(product);
                return productPrice > 0 && discountValue > productPrice;
              });
              if (productsWithInvalidDiscount.length > 0) {
                const productDetails = productsWithInvalidDiscount.map(p => {
                  const price = getProductUnitPrice(p);
                  return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                }).join(', ');
                errors.buyDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
              } else {
                const productsThatWouldBeFree = formData.buyProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue === productPrice;
                });
                if (productsThatWouldBeFree.length > 0) {
                  const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
                  errors.buyDiscountValue = `Warning: Fixed discount (£${discountValue.toFixed(2)}) is equal to the price of some products (${productNames}), which may result in those products being free.`;
                }
              }
            }
          }
        }
      } else if (formData.buyDiscountApplyTo === 'per-item') {
        formData.buyProducts.forEach((product, index) => {
          if (!product.buyItemDiscountValue || product.buyItemDiscountValue === '') {
            errors[`buyProduct_${index}_discountValue`] = 'Discount value is required for per-item discount';
          } else {
            const discountValue = parseFloat(product.buyItemDiscountValue);
            if (isNaN(discountValue)) {
              errors[`buyProduct_${index}_discountValue`] = 'Discount value must be a valid number';
            } else if (product.buyItemDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors[`buyProduct_${index}_discountValue`] =
                  'Discount percentage must be between 0 and 100';
              }
            } else if (product.buyItemDiscountType === 'fixed') {
              const productPrice = getProductUnitPrice(product);
              if (discountValue < 0) {
                errors[`buyProduct_${index}_discountValue`] = 'Fixed discount cannot be negative';
              } else if (productPrice > 0 && discountValue >= productPrice) {
                errors[`buyProduct_${index}_discountValue`] =
                  `Fixed discount (£${discountValue.toFixed(2)}) cannot exceed product price (£${productPrice.toFixed(2)})`;
              }
            }
          }
        });
      }
    }

    if (formData.getProducts.length === 0) {
      errors.getProducts = 'Please add at least one get product';
    } else if (formData.getProducts.length > 200) {
      errors.getProducts = 'You cannot add more than 200 products to get list';
    }

    formData.getProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`getProduct_${index}_type`] = 'Product Type is necessary';
      }
      if (!product.pricingType) {
        errors[`getProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
      applyZeroPriceProductError(errors, product, index, 'getProduct');
    });

    if (!formData.getQuantity || formData.getQuantity === '') {
      errors.getQuantity = 'Get quantity is required and must be a positive number';
    } else {
      const getQty = parseInt(formData.getQuantity);
      if (isNaN(getQty) || getQty < 1) {
        errors.getQuantity = 'Get quantity must be at least 1';
      } else if (getQty > 9999) {
        errors.getQuantity = 'Get quantity cannot exceed 9999';
      } else {
        const compulsoryCount = formData.getProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (p.quantity || 1), 0);
        if (compulsoryCount > getQty) {
          errors.getQuantity = `Compulsory quantity (${compulsoryCount}) cannot exceed get quantity (${getQty})`;
        }
      }
    }

    if (formData.getDiscountType === 'set-value') {
      if (!formData.getSetValue || formData.getSetValue === '') {
        errors.getSetValue = 'Please enter a set value';
      } else {
        const setValue = parseFloat(formData.getSetValue);
        if (isNaN(setValue)) {
          errors.getSetValue = 'Set value must be a valid number';
        } else if (setValue <= 0) {
          errors.getSetValue = 'Set value must be greater than 0';
        } else if (formData.getProducts.length > 0) {
          const getQty = parseInt(formData.getQuantity, 10);
          const normalizedGetQty =
            Number.isFinite(getQty) && getQty > 0 ? getQty : 1;
          const unitPrices = formData.getProducts
            .map((product) => getProductUnitPrice(product))
            .filter((price) => Number.isFinite(price) && price > 0);
          if (unitPrices.length > 0) {
            const minUnitPrice = Math.min(...unitPrices);
            const maxAllowedSetValue = minUnitPrice * normalizedGetQty;
            if (setValue > maxAllowedSetValue) {
              errors.getSetValue =
                `Set value (£${setValue.toFixed(2)}) cannot be greater than the product price (£${maxAllowedSetValue.toFixed(2)})`;
            }
          }
        }
      }
    } else if (formData.getDiscountType === 'value-off') {
      if (formData.getItemDiscountApplyTo === 'bulk') {
        if (!formData.getItemDiscountValue || formData.getItemDiscountValue === '') {
          errors.getItemDiscountValue = 'Please enter a discount value';
        } else {
          const discountValue = parseFloat(formData.getItemDiscountValue);
          if (isNaN(discountValue)) {
            errors.getItemDiscountValue = 'Discount value must be a valid number';
          } else if (formData.getItemDiscountType === 'percentage') {
            if (discountValue < 0 || discountValue > 100) {
              errors.getItemDiscountValue = 'Discount percentage must be between 0 and 100';
            }
          } else if (formData.getItemDiscountType === 'fixed') {
            if (discountValue < 0) {
              errors.getItemDiscountValue = 'Fixed discount cannot be negative';
            } else {
              const productsWithInvalidDiscount = formData.getProducts.filter((product) => {
                const productPrice = getProductUnitPrice(product);
                return productPrice > 0 && discountValue > productPrice;
              });
              if (productsWithInvalidDiscount.length > 0) {
                const productDetails = productsWithInvalidDiscount.map(p => {
                  const price = getProductUnitPrice(p);
                  return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                }).join(', ');
                errors.getItemDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
              } else {
                const productsThatWouldBeFree = formData.getProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue === productPrice;
                });
                if (productsThatWouldBeFree.length > 0) {
                  const productNames = productsThatWouldBeFree.map(p => p.name || p.product_name).join(', ');
                  errors.getItemDiscountValue = `Warning: Fixed discount (£${discountValue.toFixed(2)}) is equal to the price of some products (${productNames}), which may result in those products being free.`;
                }
              }
            }
          }
        }
      } else if (formData.getItemDiscountApplyTo === 'per-item') {
        formData.getProducts.forEach((product, index) => {
          if (!product.itemDiscountValue || product.itemDiscountValue === '') {
            errors[`getProduct_${index}_discountValue`] = 'Discount value is required for per-item discount';
          } else {
            const discountValue = parseFloat(product.itemDiscountValue);
            if (isNaN(discountValue)) {
              errors[`getProduct_${index}_discountValue`] = 'Discount value must be a valid number';
            } else if (product.itemDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors[`getProduct_${index}_discountValue`] =
                  'Discount percentage must be between 0 and 100';
              }
            } else if (product.itemDiscountType === 'fixed') {
              const productPrice = getProductUnitPrice(product);
              if (discountValue < 0) {
                errors[`getProduct_${index}_discountValue`] = 'Fixed discount cannot be negative';
              } else if (productPrice > 0 && discountValue >= productPrice) {
                errors[`getProduct_${index}_discountValue`] =
                  `Fixed discount (£${discountValue.toFixed(2)}) cannot exceed product price (£${productPrice.toFixed(2)})`;
              }
            }
          }
        });
      }
    }

    return errors;
  };

  const validateGroupOffer = () => {
    const errors = validateBuyXGetYDiscount();
    const allowed = new Set(Array.from({ length: 10 }, (_, i) => String(i + 1)));
    formData.buyProducts.forEach((product, index) => {
      const g = product.groupNo != null && String(product.groupNo).trim() !== '' ? String(product.groupNo) : '';
      if (!allowed.has(g)) {
        errors[`buyProduct_${index}_group`] = 'Group is required. Choose group 1 to 10';
      }
    });

    formData.getProducts.forEach((product, index) => {
      const g = product.groupNo != null && String(product.groupNo).trim() !== '' ? String(product.groupNo) : '';
      if (!allowed.has(g)) {
        errors[`getProduct_${index}_group`] = 'Group is required. Choose group 1 to 10';
      }
    });

    // Group qty is optional. When filled it must stay within the side total,
    // e.g. Buy 2 cannot be split as Group 1 = 2 plus Group 2 = 1 (total 3).
    const validateGroupQtyWithinTotal = (groupKeys, quantities, totalRaw, errorKey, sideLabel) => {
      let sum = 0;
      let filledCount = 0;

      groupKeys.forEach((g) => {
        const raw = quantities?.[g];
        if (raw === undefined || raw === null || String(raw).trim() === '') return;
        const qty = parseInt(raw, 10);
        if (!Number.isFinite(qty) || qty < 1) {
          errors[`${errorKey}_${g}`] = 'Qty must be at least 1';
          return;
        }
        filledCount += 1;
        sum += qty;
      });

      const total = parseInt(totalRaw, 10);
      if (filledCount > 0 && Number.isFinite(total) && total > 0 && sum > total) {
        errors[errorKey] =
          `${sideLabel} group qty total (${sum}) cannot be greater than Total ${sideLabel} Quantity (${total}).`;
      }
    };

    const buyGroups = [
      ...new Set(
        formData.buyProducts
          .map((product) => String(product.groupNo))
          .filter((g) => allowed.has(g))
      ),
    ].sort((a, b) => Number(a) - Number(b));
    validateGroupQtyWithinTotal(
      buyGroups,
      formData.buyGroupQuantities,
      formData.buyQuantity,
      'buyGroupQty',
      'Buy'
    );

    const getGroups = [
      ...new Set(
        formData.getProducts
          .map((product) => String(product.groupNo))
          .filter((g) => allowed.has(g))
      ),
    ].sort((a, b) => Number(a) - Number(b));
    validateGroupQtyWithinTotal(
      getGroups,
      formData.getGroupQuantities,
      formData.getQuantity,
      'getGroupQty',
      'Get'
    );

    return errors;
  };

  const validateBuyXDiscount = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one product to the promotion';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = `Maximum 200 products allowed. Current: ${formData.buyProducts.length} products`;
    }

    formData.buyProducts.forEach((product, index) => {
      if (!product.type) {
        errors[`buyProduct_${index}_type`] = 'Please select product type (Box or Piece)';
      }
      if (!product.pricingType) {
        errors[`buyProduct_${index}_pricing`] = 'Please select price type (Sales Price or Offer Price)';
      }
      applyZeroPriceProductError(errors, product, index, 'buyProduct');
      if (product.compulsory && (!product.quantity || product.quantity === '' || parseInt(product.quantity) < 1)) {
        errors[`buyProduct_${index}_quantity`] = 'Quantity is required and must be at least 1 for compulsory products';
      }
    });

    if (!formData.buyQuantity || formData.buyQuantity === '') {
      errors.buyQuantity = 'Buy quantity is required and must be a positive whole number';
    } else {
      const buyQty = parseInt(formData.buyQuantity);
      if (isNaN(buyQty) || buyQty < 1) {
        errors.buyQuantity = 'Buy quantity is required and must be a positive whole number';
      } else if (buyQty > 9999) {
        errors.buyQuantity = 'Buy quantity must be between 1 and 9999';
      } else if (buyQty < 1) {
        errors.buyQuantity = 'Buy quantity must be between 1 and 9999';
      } else {
        const compulsoryCount = formData.buyProducts
          .filter((p) => p.compulsory)
          .reduce((sum, p) => sum + (parseInt(p.quantity) || 1), 0);
        if (compulsoryCount > 0 && compulsoryCount > buyQty) {
          errors.compulsoryQuantity = `Total compulsory quantity (${compulsoryCount}) must be less than or equal to buy quantity (${buyQty})`;
        }
      }
    }

    if (formData.buyDiscountConfigType === 'set-value') {
      if (!formData.buySetValue || formData.buySetValue === '') {
        errors.buySetValue = 'Please enter a set value for the promotion';
      } else {
        const setValue = parseFloat(formData.buySetValue);
        if (isNaN(setValue)) {
          errors.buySetValue = 'Please enter a valid numeric value';
        } else if (setValue <= 0) {
          errors.buySetValue = 'Set value must be greater than 0';
        } else if (formData.buyProducts.length > 0) {
          const buyQty = parseInt(formData.buyQuantity, 10);
          const normalizedBuyQty =
            Number.isFinite(buyQty) && buyQty > 0 ? buyQty : 1;
          const unitPrices = formData.buyProducts
            .map((product) => getProductUnitPrice(product))
            .filter((price) => Number.isFinite(price) && price > 0);
          if (unitPrices.length > 0) {
            const minUnitPrice = Math.min(...unitPrices);
            const maxAllowedSetValue = minUnitPrice * normalizedBuyQty;
            if (setValue > maxAllowedSetValue) {
              errors.buySetValue =
                `Set value (£${setValue.toFixed(2)}) cannot be greater than the product price (£${maxAllowedSetValue.toFixed(2)})`;
            }
          }
        }
      }
    } else if (formData.buyDiscountConfigType === 'value-off') {
      if (formData.buyXDiscountApplyTo === 'bulk') {
        if (!formData.buyXDiscountType || formData.buyXDiscountType === 'no-discount') {
          errors.buyXDiscountType = 'Please select discount type (Percentage or Fixed Amount)';
        } else {
          if (!formData.buyXDiscountValue || formData.buyXDiscountValue === '') {
            errors.buyXDiscountValue = 'Please enter a discount value';
          } else {
            const discountValue = parseFloat(formData.buyXDiscountValue);
            if (isNaN(discountValue)) {
              errors.buyXDiscountValue = 'Please enter a valid numeric value';
            } else if (formData.buyXDiscountType === 'percentage') {
              if (discountValue < 0 || discountValue > 100) {
                errors.buyXDiscountValue = 'Percentage must be between 0 and 100';
              }
            } else if (formData.buyXDiscountType === 'fixed') {
              if (discountValue < 0) {
                errors.buyXDiscountValue = 'Discount amount cannot be negative';
              } else {
                const productsWithInvalidDiscount = formData.buyProducts.filter((product) => {
                  const productPrice = getProductUnitPrice(product);
                  return productPrice > 0 && discountValue > productPrice;
                });
                if (productsWithInvalidDiscount.length > 0) {
                  const productDetails = productsWithInvalidDiscount.map(p => {
                    const price = getProductUnitPrice(p);
                    return `${p.name || p.product_name} (£${price.toFixed(2)})`;
                  }).join(', ');
                  errors.buyXDiscountValue = `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price. Invalid products: ${productDetails}`;
                }
              }
            }
          }
        }
      } else if (formData.buyXDiscountApplyTo === 'per-item') {
        formData.buyProducts.forEach((product, index) => {
          if (product.buyXItemDiscountType && product.buyXItemDiscountType !== 'no-discount') {
            if (!product.buyXItemDiscountValue || product.buyXItemDiscountValue === '') {
              errors[`buyProduct_${index}_discountValue`] = 'Please enter a discount value';
            } else {
              const discountValue = parseFloat(product.buyXItemDiscountValue);
              const productPrice = getProductUnitPrice(product);

              if (isNaN(discountValue)) {
                errors[`buyProduct_${index}_discountValue`] = 'Please enter a valid numeric value';
              } else if (product.buyXItemDiscountType === 'percentage') {
                if (discountValue < 0 || discountValue > 100) {
                  errors[`buyProduct_${index}_discountValue`] = 'Percentage must be between 0 and 100';
                }
              } else if (product.buyXItemDiscountType === 'fixed') {
                if (discountValue < 0) {
                  errors[`buyProduct_${index}_discountValue`] = 'Discount amount cannot be negative';
                } else {
                  const productPrice = getProductUnitPrice(product);
                  if (productPrice > 0 && discountValue > productPrice) {
                    const productLabel =
                      product.name || product.product_name || product.code || `Product ${index + 1}`;
                    errors[`buyProduct_${index}_discountValue`] =
                      `Discount amount (£${discountValue.toFixed(2)}) must be less than or equal to the product price (£${productPrice.toFixed(2)}). Invalid product: ${productLabel}`;
                  }
                }
              }
            }
          }
        });
      }
    }

    return errors;
  };

  const validateMultiLayerOffer = () => {
    const errors = {};

    if (formData.buyProducts.length === 0) {
      errors.buyProducts = 'Please add at least one product for the multi-layer offer';
    } else if (formData.buyProducts.length > 200) {
      errors.buyProducts = 'Cannot exceed 200 products in a multi-layer offer';
    }

    const buyProductType = formData.buyProductType || 'box-only';

    let referenceBoxPrice = 0;
    let referencePiecePrice = 0;

    if (formData.buyProducts.length > 0) {
      const buyPricingType = formData.buyPricingType || 'sales';
      referenceBoxPrice = getMinMultiLayerBuyPrice(
        formData.buyProducts,
        'Box',
        buyPricingType
      );
      referencePiecePrice = getMinMultiLayerBuyPrice(
        formData.buyProducts,
        'Piece',
        buyPricingType
      );
      const firstProduct = formData.buyProducts[0];
      const epsilon = 0.005;

      formData.buyProducts.forEach((product, index) => {
        const split = product.split;
        let isValidForProductType = true;

        if (buyProductType === 'box-only' && split !== 0 && split !== 2) {
          isValidForProductType = false;
          errors[`buyProduct_${index}_type`] = 'This product does not support Box type. Please remove and select compatible products.';
        } else if (buyProductType === 'piece-only' && split !== 1 && split !== 2) {
          isValidForProductType = false;
          errors[`buyProduct_${index}_type`] = 'This product does not support Piece type. Please remove and select compatible products.';
        }

        if (isValidForProductType && !product.type) {
          errors[`buyProduct_${index}_type`] = 'Please select product type: Box, Piece, or Both';
        }
        applyMultiLayerBuyZeroPriceErrors(errors, product, index);
      });
    }

    if (!formData.slabs || formData.slabs.length === 0) {
      errors.slabs = 'Please add at least one slab configuration';
      return errors;
    }

    const boxRanges = [];
    const pieceRanges = [];
    const packSizeRaw =
      formData.buyProducts.length > 0
        ? formData.buyProducts[0].pack_size ||
        formData.buyProducts[0].unit_per_box ||
        formData.buyProducts[0].pack ||
        1
        : 1;
    const packSize = parseInt(packSizeRaw, 10) > 0 ? parseInt(packSizeRaw, 10) : 1;

    formData.slabs.forEach((slab, index) => {
      if (!slab.slabName || slab.slabName.trim() === '') {
        errors[`slab_${index}_name`] = 'Slab name is required';
      }

      const hasBoxRange =
        (slab.productType === 'box-only' || slab.productType === 'both') &&
        slab.boxMinQuantity !== '' &&
        slab.boxMaxQuantity !== '';
      const hasPieceRange =
        (slab.productType === 'piece-only' || slab.productType === 'both') &&
        slab.pieceMinQuantity !== '' &&
        slab.pieceMaxQuantity !== '';

      if (!hasBoxRange && !hasPieceRange) {
        errors[`slab_${index}_range`] = 'At least one range (box OR piece) must be defined';
      }

      if (slab.boxMinQuantity !== '' || slab.boxMaxQuantity !== '') {
        const boxMin = parseInt(slab.boxMinQuantity);
        const boxMax = parseInt(slab.boxMaxQuantity);

        if (isNaN(boxMin) || isNaN(boxMax)) {
          errors[`slab_${index}_boxRange`] = 'Please enter a valid box quantity';
        } else if (boxMin > boxMax) {
          errors[`slab_${index}_boxRange`] = 'Minimum quantity must be less than or equal to maximum quantity';
        } else {
          boxRanges.push({ slabIndex: index, min: boxMin, max: boxMax, packSize });
        }
      }

      if (!slab.discountType) {
        errors[`slab_${index}_discountType`] = 'Please select discount type';
      }

      if (slab.discountType === 'set-value') {
        if (buyProductType === 'box-only' || buyProductType === 'both') {
          if (!slab.boxProductsValue || slab.boxProductsValue === '') {
            errors[`slab_${index}_boxValue`] = 'Please enter set value for box products';
          } else {
            const boxValue = parseFloat(slab.boxProductsValue);
            if (isNaN(boxValue)) {
              errors[`slab_${index}_boxValue`] = 'Please enter a valid numeric value';
            } else if (boxValue <= 0) {
              errors[`slab_${index}_boxValue`] = 'Set value for boxes must be greater than 0';
            }
          }
        }

        if (buyProductType === 'piece-only' || buyProductType === 'both') {
          if (!slab.pieceProductsValue || slab.pieceProductsValue === '') {
            errors[`slab_${index}_pieceValue`] = 'Please enter set value for piece products';
          } else {
            const pieceValue = parseFloat(slab.pieceProductsValue);
            if (isNaN(pieceValue)) {
              errors[`slab_${index}_pieceValue`] = 'Please enter a valid numeric value';
            } else if (pieceValue <= 0) {
              errors[`slab_${index}_pieceValue`] = 'Set value for pieces must be greater than 0';
            }
          }
        }
      } else if (slab.discountType === 'percentage' || slab.discountType === 'fixed') {
        if (buyProductType === 'box-only' || buyProductType === 'both') {
          if (!slab.boxProductsValue || slab.boxProductsValue === '') {
            errors[`slab_${index}_boxValue`] = 'Please enter discount value for box products';
          } else {
            const boxValue = parseFloat(slab.boxProductsValue);
            if (isNaN(boxValue)) {
              errors[`slab_${index}_boxValue`] = 'Please enter a valid numeric value';
            } else if (boxValue < 0) {
              errors[`slab_${index}_boxValue`] = 'Discount value cannot be negative';
            } else if (slab.discountType === 'percentage' && boxValue > 100) {
              errors[`slab_${index}_boxValue`] = 'Percentage must be between 0 and 100';
            } else if (
              slab.discountType === 'fixed' &&
              referenceBoxPrice > 0 &&
              boxValue > referenceBoxPrice
            ) {
              errors[`slab_${index}_boxValue`] =
                `Fixed discount (£${boxValue.toFixed(2)}) cannot exceed the lowest product price (£${referenceBoxPrice.toFixed(2)})`;
            }
          }
        }

        if (buyProductType === 'piece-only' || buyProductType === 'both') {
          if (!slab.pieceProductsValue || slab.pieceProductsValue === '') {
            errors[`slab_${index}_pieceValue`] = 'Please enter discount value for piece products';
          } else {
            const pieceValue = parseFloat(slab.pieceProductsValue);
            if (isNaN(pieceValue)) {
              errors[`slab_${index}_pieceValue`] = 'Please enter a valid numeric value';
            } else if (pieceValue < 0) {
              errors[`slab_${index}_pieceValue`] = 'Discount value cannot be negative';
            } else if (slab.discountType === 'percentage' && pieceValue > 100) {
              errors[`slab_${index}_pieceValue`] = 'Percentage must be between 0 and 100';
            } else if (
              slab.discountType === 'fixed' &&
              referencePiecePrice > 0 &&
              pieceValue > referencePiecePrice
            ) {
              errors[`slab_${index}_pieceValue`] =
                `Fixed discount (£${pieceValue.toFixed(2)}) cannot exceed the lowest product price (£${referencePiecePrice.toFixed(2)})`;
            }
          }
        }
      }

      if (slab.pieceMinQuantity !== '' || slab.pieceMaxQuantity !== '') {
        const pieceMin = parseInt(slab.pieceMinQuantity);
        const pieceMax = parseInt(slab.pieceMaxQuantity);

        if (isNaN(pieceMin) || isNaN(pieceMax)) {
          errors[`slab_${index}_pieceRange`] = 'Please enter a valid piece quantity';
        } else if (pieceMin > pieceMax) {
          errors[`slab_${index}_pieceRange`] = 'Minimum quantity must be less than or equal to maximum quantity';
        } else {
          pieceRanges.push({ slabIndex: index, min: pieceMin, max: pieceMax });
        }
      }

      if (slab.hasGetProducts) {
        if (!slab.getQuantity || slab.getQuantity === '') {
          errors[`slab_${index}_getQuantity`] = 'Get quantity is required and must be a positive number';
        } else {
          const getQty = parseInt(slab.getQuantity);
          if (isNaN(getQty) || getQty < 1) {
            errors[`slab_${index}_getQuantity`] = 'Get quantity must be at least 1';
          } else if (getQty > 9999) {
            errors[`slab_${index}_getQuantity`] = 'Get quantity cannot exceed 9999';
          }
        }
        if (!slab.getProducts || slab.getProducts.length === 0) {
          errors[`slab_${index}_getProducts`] = 'Please add at least one get product';
        } else {
          slab.getProducts.forEach((product, pIndex) => {
            if (!product.type) {
              errors[`slab_${index}_getProduct_${pIndex}_type`] = 'Please select pricing for get products';
            }
            if (product.pricingType === 'offer' && getProductUnitPrice(product) === 0) {
              errors[`slab_${index}_getProduct_${pIndex}_offerPrice`] = 'Get products will be free. Please confirm.';
            }
          });
        }
      }
    });

    Object.assign(errors, buildSlabGetQuantityCompulsoryErrors(formData.slabs));

    const overlapMessages = [];

    for (let i = 0; i < boxRanges.length; i++) {
      for (let j = i + 1; j < boxRanges.length; j++) {
        const range1 = boxRanges[i];
        const range2 = boxRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          const slab1Name = formData.slabs[range1.slabIndex]?.slabName || `Slab ${range1.slabIndex + 1}`;
          const slab2Name = formData.slabs[range2.slabIndex]?.slabName || `Slab ${range2.slabIndex + 1}`;
          overlapMessages.push(`Box range [${range1.min}-${range1.max}] in [${slab1Name}] overlaps with [${slab2Name}]`);
        }
      }
    }

    for (let i = 0; i < pieceRanges.length; i++) {
      for (let j = i + 1; j < pieceRanges.length; j++) {
        const range1 = pieceRanges[i];
        const range2 = pieceRanges[j];
        if (
          (range1.min <= range2.max && range1.max >= range2.min) ||
          (range2.min <= range1.max && range2.max >= range1.min)
        ) {
          const slab1Name = formData.slabs[range1.slabIndex]?.slabName || `Slab ${range1.slabIndex + 1}`;
          const slab2Name = formData.slabs[range2.slabIndex]?.slabName || `Slab ${range2.slabIndex + 1}`;
          overlapMessages.push(`Piece range [${range1.min}-${range1.max}] in [${slab1Name}] overlaps with [${slab2Name}]`);
        }
      }
    }

    if (buyProductType === 'both') {
      const packSizeForValidation = packSize;
      for (let i = 0; i < formData.slabs.length; i++) {
        const slab1 = formData.slabs[i];
        if (
          slab1.productType === 'both' &&
          slab1.boxMinQuantity && slab1.boxMaxQuantity &&
          slab1.pieceMinQuantity && slab1.pieceMaxQuantity
        ) {
          const box1Min = parseInt(slab1.boxMinQuantity);
          const box1Max = parseInt(slab1.boxMaxQuantity);
          const piece1Min = parseInt(slab1.pieceMinQuantity);
          const piece1Max = parseInt(slab1.pieceMaxQuantity);
          const box1MinInPieces = box1Min * packSizeForValidation;
          const box1MaxInPieces = box1Max * packSizeForValidation;

          for (let j = i + 1; j < formData.slabs.length; j++) {
            const slab2 = formData.slabs[j];
            if (
              slab2.productType === 'both' &&
              slab2.boxMinQuantity && slab2.boxMaxQuantity &&
              slab2.pieceMinQuantity && slab2.pieceMaxQuantity
            ) {
              const box2Min = parseInt(slab2.boxMinQuantity);
              const box2Max = parseInt(slab2.boxMaxQuantity);
              const piece2Min = parseInt(slab2.pieceMinQuantity);
              const piece2Max = parseInt(slab2.pieceMaxQuantity);
              const box2MinInPieces = box2Min * packSizeForValidation;
              const box2MaxInPieces = box2Max * packSizeForValidation;

              const slab1Name = slab1.slabName || `Slab ${i + 1}`;
              const slab2Name = slab2.slabName || `Slab ${j + 1}`;

              const boxOverlap = (box1Min <= box2Max && box1Max >= box2Min);
              const pieceOverlap = (piece1Min <= piece2Max && piece1Max >= piece2Min);
              const boxPieceOverlap1 = (box1MinInPieces <= piece2Max && box1MaxInPieces >= piece2Min);
              const boxPieceOverlap2 = (box2MinInPieces <= piece1Max && box2MaxInPieces >= piece1Min);

              if (boxOverlap || pieceOverlap || boxPieceOverlap1 || boxPieceOverlap2) {
                if (boxOverlap) {
                  overlapMessages.push(`Box range [${box1Min}-${box1Max}] in [${slab1Name}] overlaps with box range [${box2Min}-${box2Max}] in [${slab2Name}]`);
                }
                if (pieceOverlap) {
                  overlapMessages.push(`Piece range [${piece1Min}-${piece1Max}] in [${slab1Name}] overlaps with piece range [${piece2Min}-${piece2Max}] in [${slab2Name}]`);
                }
                if (boxPieceOverlap1) {
                  overlapMessages.push(`Box range [${box1Min}-${box1Max}] (${box1MinInPieces}-${box1MaxInPieces} pieces) in [${slab1Name}] overlaps with piece range [${piece2Min}-${piece2Max}] in [${slab2Name}]`);
                }
                if (boxPieceOverlap2) {
                  overlapMessages.push(`Piece range [${piece1Min}-${piece1Max}] in [${slab1Name}] overlaps with box range [${box2Min}-${box2Max}] (${box2MinInPieces}-${box2MaxInPieces} pieces) in [${slab2Name}]`);
                }
              }
            }
          }
        }
      }
    }

    if (overlapMessages.length > 0) {
      errors.slabOverlap = overlapMessages.length === 1
        ? `Range overlaps with existing slab. ${overlapMessages[0]}. Please adjust.`
        : `Multiple overlaps detected: ${overlapMessages.join('; ')}. Please adjust all ranges.`;
    }

    return errors;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (formData.description.length > 500) newErrors.description = 'Description cannot exceed 500 characters';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate) {
      const startDay = moment(formData.startDate).startOf('day');
      const endDay = moment(formData.endDate).startOf('day');
      if (endDay.isBefore(startDay)) {
        newErrors.endDate = 'End date cannot be before start date';
      } else {
        const daysDifference = endDay.diff(startDay, 'days');
        if (daysDifference > 365) {
          newErrors.endDate = 'Promotion period cannot exceed 1 year. Please adjust your dates.';
        }
      }
    }
    if (formData.selectedChannels.length === 0)
      newErrors.channels = 'At least one channel is required';

    if (formData.promotionType === 'buy-x-get-y-free') {
      const promotionErrors = validateBuyXGetYFree();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'buy-x-get-y-discount') {
      const promotionErrors = validateBuyXGetYDiscount();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'buy-x-discount') {
      const promotionErrors = validateBuyXDiscount();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'multi-layer-offer') {
      const promotionErrors = validateMultiLayerOffer();
      Object.assign(newErrors, promotionErrors);
    } else if (formData.promotionType === 'group-offer') {
      const promotionErrors = validateGroupOffer();
      Object.assign(newErrors, promotionErrors);
    }

    setErrors(newErrors);
    return newErrors;
  };

  const transformFormDataToAPI = () => {
    const basePayload = {
      title: formData.title,
      description: formData.description,
      start_date: formData.startDate ? moment(formData.startDate).format('YYYY-MM-DD') : '',
      end_date: formData.endDate ? moment(formData.endDate).format('YYYY-MM-DD') : '',
      start_time: formData.startTime ? moment(formData.startTime).format('HH:mm:ss') : '00:00:00',
      end_time: '23:59:59',
      channels: formData.selectedChannels,
      promotion_type: formData.promotionType,
      status: 'active',
    };

    if (formData.promotionType === 'multi-layer-offer') {
      basePayload.buy_pricing_type = toApiPromotionPricingType(formData.buyPricingType || 'sales');
    }

    let config = {};

    if (formData.promotionType === 'buy-x-get-y-free') {
      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          is_compulsory: product.compulsory || false,
        })),
        get_products: formData.getProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          is_compulsory: product.compulsory || false,
        })),
      };
    } else if (formData.promotionType === 'buy-x-get-y-discount') {

      const getDiscount = {
        discount_config_type: formData.getDiscountType || 'set-value',
        discount_type:
          formData.getDiscountType === 'set-value'
            ? 'set-value'
            : formData.getItemDiscountType || 'percentage',
        discount_value:
          formData.getDiscountType === 'set-value'
            ? parseFloat(formData.getSetValue) || 0
            : parseFloat(formData.getItemDiscountValue) || 0,
        apply_to:
          formData.getDiscountType === 'set-value'
            ? ''
            : (formData.getItemDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN'),
      };

      const buyDiscountConfig = {};

      // Only include buy_discount when discount is actually enabled (not 'none')
      if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
        buyDiscountConfig.buy_discount = {
          apply_to: formData.buyDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
          discount_type: formData.buyDiscountType || 'percentage',
          discount_value: parseFloat(formData.buyDiscountValue) || 0,
        };
      }

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        ...buyDiscountConfig,
        get_discount: getDiscount,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          item_discount:
            formData.buyDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.buyItemDiscountType || 'percentage',
                discount_value: parseFloat(product.buyItemDiscountValue) || 0,
              }
              : null,
        })),
        get_products: formData.getProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          item_discount:
            formData.getItemDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.itemDiscountType || 'percentage',
                discount_value: parseFloat(product.itemDiscountValue) || 0,
              }
              : null,
        })),
      };
    } else if (formData.promotionType === 'buy-x-discount') {
      const buyDiscountConfig = {};

      // Only add buy_discount when buyDiscountConfigType is 'set-value'
      if (formData.buyDiscountConfigType === 'set-value') {
        buyDiscountConfig.buy_discount = {
          discount_config_type: 'set-value',
          discount_type: 'set-value',
          discount_value: parseFloat(formData.buySetValue) || 0,
          apply_to: '',
        };
      } else if (formData.buyDiscountConfigType === 'value-off') {
        // When value-off is selected, use item_discount with the discount type
        buyDiscountConfig.buy_discount = {
          discount_config_type: 'value-off',
          discount_type: formData.buyXDiscountType || 'percentage',
          discount_value: parseFloat(formData.buyXDiscountValue) || 0,
          apply_to: formData.buyXDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
        };
      }

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        ...buyDiscountConfig,
        buy_products: formData.buyProducts.map((product) => ({
          product_id: product.id,
          product_type: product.type,
          pricing_type: product.pricingType || 'sales',
          is_compulsory: product.compulsory || false,
          quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
          item_discount:
            formData.buyXDiscountApplyTo === 'per-item'
              ? {
                discount_type: product.buyXItemDiscountType || 'percentage',
                discount_value: parseFloat(product.buyXItemDiscountValue) || 0,
              }
              : null,
        })),
      };
    } else if (formData.promotionType === 'multi-layer-offer') {
      const isBoxOnly = formData.buyProductType === 'box-only';
      const buyPricingType = formData.buyPricingType || 'sales';
      const slabs = formData.slabs || [];
      const parseQty = (val) => {
        const num = parseInt(val, 10);
        return Number.isFinite(num) && num >= 0 ? num : 0;
      };
      const buildThresholdRangeMap = (slabList, minKey, maxKey) => {
        const thresholdRows = slabList
          .map((slab, index) => {
            const fromValue = parseQty(slab[minKey]);
            const toValue = parseQty(slab[maxKey]);
            return {
              index,
              threshold: toValue,
              isSingleValue: fromValue > 0 && fromValue === toValue,
            };
          })
          .filter((row) => row.isSingleValue)
          .sort((a, b) => a.threshold - b.threshold);

        const rangeMap = new Map();
        thresholdRows.forEach((row, idx) => {
          const next = thresholdRows[idx + 1];
          rangeMap.set(row.index, {
            from: row.threshold,
            // to: next ? Math.max(row.threshold, next.threshold - 1) : row.threshold,
            to: row.threshold,
          });
        });
        return rangeMap;
      };
      const boxThresholdRangeMap = buildThresholdRangeMap(slabs, 'boxMinQuantity', 'boxMaxQuantity');
      const pieceThresholdRangeMap = buildThresholdRangeMap(slabs, 'pieceMinQuantity', 'pieceMaxQuantity');

      config = {
        product_type: formData.buyProductType || 'box-only',
        buy_pricing_type: toApiPromotionPricingType(buyPricingType),
        buy_products: formData.buyProducts.map((product) => {
          const buyProduct = {
            product_id: product.id,
            product_type: product.type || 'Box',
            box_pricing_type: toApiPromotionPricingType(
              getMultiLayerBuyPricingMode(product, 'Box', buyPricingType)
            ),
            is_compulsory: product.compulsory || false,
          };
          if (!isBoxOnly) {
            buyProduct.piece_pricing_type = toApiPromotionPricingType(
              getMultiLayerBuyPricingMode(product, 'Piece', buyPricingType)
            );
          }
          return buyProduct;
        }),
        slabs: slabs.map((slab, index) => {
          const boxFrom = parseQty(slab.boxMinQuantity);
          const boxTo = parseQty(slab.boxMaxQuantity);
          const pieceFrom = parseQty(slab.pieceMinQuantity);
          const pieceTo = parseQty(slab.pieceMaxQuantity);
          const normalizedBoxRange = boxThresholdRangeMap.get(index) || { from: boxFrom, to: boxTo };
          const normalizedPieceRange = pieceThresholdRangeMap.get(index) || { from: pieceFrom, to: pieceTo };

          const slabData = {
            slab_number: index + 1,
            slab_name: slab.slabName,
            slab_order: slab.slabOrder || index + 1,
            product_type: formData.buyProductType || 'box-only',
            box_quantity_range: {
              from: normalizedBoxRange.from,
              to: normalizedBoxRange.to,
            },
            box_pricing_type: toApiPromotionPricingType(buyPricingType),
            box_discount_type: slab.discountType || 'percentage',
            box_discount_value: parseFloat(slab.boxProductsValue) || 0,
            include_get_products: slab.hasGetProducts || false,
            get_quantity: slab.hasGetProducts ? (parseInt(slab.getQuantity) || 0) : 0,
            get_products: slab.hasGetProducts
              ? (slab.getProducts || []).map((product) => ({
                product_id: product.id,
                product_type: product.type || 'Piece',
                quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
                is_compulsory: product.compulsory || false,
                compulsory_quantity: product.compulsory ? (parseInt(product.quantity) || 1) : 0,
                pricing_type: toApiPromotionPricingType(product.pricingType || buyPricingType),
                discount_type: 'free',
                discount_value: 0,
              }))
              : [],
          };
          if (!isBoxOnly) {
            slabData.piece_quantity_range = {
              from: normalizedPieceRange.from,
              to: normalizedPieceRange.to,
            };
            slabData.piece_pricing_type = toApiPromotionPricingType(buyPricingType);
            slabData.piece_discount_type = slab.discountType || 'percentage';
            slabData.piece_discount_value = parseFloat(slab.pieceProductsValue) || 0;
          }
          return slabData;
        }),
      };
    } else if (formData.promotionType === 'group-offer') {
      const getDiscount = {
        discount_config_type: formData.getDiscountType || 'set-value',
        discount_type:
          formData.getDiscountType === 'set-value'
            ? 'set-value'
            : formData.getItemDiscountType || 'percentage',
        discount_value:
          formData.getDiscountType === 'set-value'
            ? parseFloat(formData.getSetValue) || 0
            : parseFloat(formData.getItemDiscountValue) || 0,
        apply_to:
          formData.getDiscountType === 'set-value'
            ? ''
            : (formData.getItemDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN'),
      };

      const buyDiscountConfig = {};

      if (formData.buyDiscountApplyTo && formData.buyDiscountApplyTo !== 'none') {
        buyDiscountConfig.buy_discount = {
          apply_to: formData.buyDiscountApplyTo === 'per-item' ? 'ITEM_WISE' : 'BULK_ASSIGN',
          discount_type: formData.buyDiscountType || 'percentage',
          discount_value: parseFloat(formData.buyDiscountValue) || 0,
        };
      }

      const groupIdFromProduct = (product) => {
        const n = parseInt(String(product.groupNo), 10);
        return Number.isFinite(n) && n >= 1 && n <= 10 ? String(n) : null;
      };

      const buyGroupQuantities = formData.buyGroupQuantities || {};
      const getGroupQuantities = formData.getGroupQuantities || {};
      // Group qty is optional; 0 lets the engine fall back to its default of one per group.
      const resolveGroupQty = (groupId, map) => {
        if (!groupId) return 0;
        const qty = parseInt(map[groupId], 10);
        return Number.isFinite(qty) && qty > 0 ? qty : 0;
      };

      config = {
        buy_quantity: parseInt(formData.buyQuantity) || 0,
        get_quantity: parseInt(formData.getQuantity) || 0,
        ...buyDiscountConfig,
        get_discount: getDiscount,
        buy_products: formData.buyProducts.map((product) => {
          const groupId = groupIdFromProduct(product);
          return {
            product_id: String(product.id),
            product_type: product.type,
            pricing_type: product.pricingType || 'sales',
            is_compulsory: product.compulsory || false,
            quantity: resolveGroupQty(groupId, buyGroupQuantities),
            ...(groupId != null ? { group_id: groupId } : {}),
            item_discount:
              formData.buyDiscountApplyTo === 'per-item'
                ? {
                  discount_type: product.buyItemDiscountType || 'percentage',
                  discount_value: parseFloat(product.buyItemDiscountValue) || 0,
                }
                : null,
          };
        }),
        get_products: formData.getProducts.map((product) => {
          const groupId = groupIdFromProduct(product);
          return {
            product_id: String(product.id),
            product_type: product.type,
            pricing_type: product.pricingType || 'sales',
            is_compulsory: product.compulsory || false,
            quantity: resolveGroupQty(groupId, getGroupQuantities),
            ...(groupId != null ? { group_id: groupId } : {}),
            item_discount:
              formData.getItemDiscountApplyTo === 'per-item'
                ? {
                  discount_type: product.itemDiscountType || 'percentage',
                  discount_value: parseFloat(product.itemDiscountValue) || 0,
                }
                : null,
          };
        }),
      };
    }

    return {
      ...basePayload,
      config,
    };
  };

  const checkForZeroPriceProducts = () => {
    const productsWithZeroPrice = [];

    const checkProduct = (product, listType) => {
      const pricingType = product.pricingType || 'sales';
      const price = parseFloat(getProductUnitPrice(product)) || 0;

      if (price === 0) {
        productsWithZeroPrice.push({
          name: product.name,
          code: product.code,
          pricingType,
          listType,
        });
      }
    };

    const promoType = formData.promotionType;

    if (promoType === 'buy-x-discount') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
      // Ignore get products from previous configuration
    } else if (promoType === 'buy-x-get-y-discount') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
      formData.getProducts?.forEach(product => checkProduct(product, 'Get Products'));
    } else if (promoType === 'buy-x-get-y-free') {
      // Get items are intentionally free; do not flag zero pricing
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'multi-layer-offer') {
      // Only check shared buy products; slab get products are typically free or discounted contextually
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
    } else if (promoType === 'group-offer') {
      formData.buyProducts?.forEach(product => checkProduct(product, 'Buy Products'));
      formData.getProducts?.forEach(product => checkProduct(product, 'Get Products'));
    }
    return productsWithZeroPrice;
  };

  const handleSave = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      toast.error('Please fix all validation errors before submitting');
      return;
    }

    setSaving(true);
    try {
      const payload = transformFormDataToAPI();

      const response = await createPromotion(payload);

      if (response.success) {
        toast.success('Promotion created successfully!');
        setTimeout(() => {
          navigate('/Promotions/Promos');
        }, 1500);
      } else {
        toast.error(formatCreatePromotionErrorResponse(response));
      }
    } catch (error) {
      const errorMsg = error.message || 'Unknown error';
      const cleanErrorMsg = typeof errorMsg === 'string'
        ? errorMsg.replace(/^Validation error:\s*/i, '')
        : errorMsg;
      toast.error(cleanErrorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmZeroPriceWarning = async () => {
    setShowZeroPriceWarning(false);
    setZeroPriceProducts([]);
    setSaving(true);
    try {
      const payload = transformFormDataToAPI();

      const response = await createPromotion(payload);

      if (response.success) {
        toast.success('Promotion created successfully!');
        setTimeout(() => {
          navigate('/Promotions/Promos');
        }, 1500);
      } else {
        toast.error(formatCreatePromotionErrorResponse(response));
      }
    } catch (error) {
      const errorMsg = error.message || 'Unknown error';
      const cleanErrorMsg = typeof errorMsg === 'string'
        ? errorMsg.replace(/^Validation error:\s*/i, '')
        : errorMsg;
      toast.error(cleanErrorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelZeroPriceWarning = () => {
    setShowZeroPriceWarning(false);
    setZeroPriceProducts([]);
  };

  const handleCancel = () => {
    navigate('/Promotions/Promos');
  };

  return (
    <Box>
      <Box sx={{ width: "100%", p: 1.5, backgroundColor: "#f5f7fa" }}>
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(145, 158, 171, 0.16)",
            overflow: "hidden",
            backgroundColor: "#fff",
            p: 2,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack spacing={1}>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    color: "#212b36",
                    mb: 1,
                  }}
                >
                  Promotion Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      className="form-input-border"
                      fullWidth
                      label="Promotion Title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      error={!!errors.title}
                      helperText={errors.title}
                      placeholder="Enter promotion title"
                      inputProps={{ maxLength: 100 }}

                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <DatePicker
                      className="form-input-border"
                      label="Start Date"
                      value={formData.startDate}
                      onChange={(date) => handleInputChange('startDate', date)}
                      format="DD-MM-YYYY"
                      minDate={moment()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.startDate,
                          helperText: errors.startDate,
                          placeholder: 'DD-MM-YYYY',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <DatePicker
                      label="End Date"
                      className="form-input-border"
                      value={formData.endDate}
                      onChange={(date) => handleInputChange('endDate', date)}
                      format="DD-MM-YYYY"
                      minDate={formData.startDate || moment()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.endDate,
                          helperText: errors.endDate,
                          placeholder: 'DD-MM-YYYY',

                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      className="form-input-border"
                      label="Promotion Description"
                      value={formData.description}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 500) {
                          handleInputChange('description', value);
                        }
                      }}
                      error={!!errors.description}
                      helperText={
                        errors.description ||
                        `${formData.description.length}/500 characters`
                      }
                      sx={{
                        '& .MuiFormHelperText-root': {
                          color: "#000",
                        },
                      }}
                      placeholder="Enter customer-facing description"
                      multiline
                      minRows={2}
                      maxRows={5}
                      inputProps={{ maxLength: 500,
                       }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        color: "#212b36",
                      }}
                    >
                      Promotion Channel
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                      {channels.map((channel) => {
                        const isChecked = channel.isMobileApp
                          ? mobileAppChannelValue && formData.selectedChannels.includes(mobileAppChannelValue)
                          : formData.selectedChannels.includes(channel.value);
                        return (
                          <FormControlLabel
                            key={channel.id}
                            control={
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handleChannelToggle(channel.value)}
                                sx={{
                                  color: '#F8991D',
                                  '&.Mui-checked': {
                                    color: '#F8991D',
                                  },
                                }}
                              />
                            }
                            label={channel.label}
                          />
                        );
                      })}
                    </Stack>
                    {errors.channels && (
                      <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                        {errors.channels}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    color: "#212b36",
                    mb: 0.5,
                  }}
                >
                  Select Promotion Type
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    width: '100%',
                    alignItems: 'stretch',
                    gridTemplateColumns: {
                      xs: 'minmax(0, 1fr)',
                      sm: 'repeat(3, minmax(0, 1fr))',
                      md: 'repeat(3, minmax(0, 1fr))',
                      lg: 'repeat(5, minmax(0, 1fr))',
                    },
                  }}
                >
                  {promotionTypes.map((type) => (
                    <Card
                      key={type.id}
                      onClick={() => handleInputChange('promotionType', type.id)}
                      elevation={0}

                      sx={{
                        bgcolor: formData.promotionType === type.id ? '#e3f2fd' : '#f5f7fa',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        height: '100%',
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 0,
                        border: formData.promotionType === type.id ? '2px solid #2196f3' : '1px solid rgba(59, 59, 59, 0.94)',
                      }}
                    >
                      <Box
                        sx={{
                          p: 2,
                          flex: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          textAlign: 'left',
                          minHeight: 0,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            lineHeight: 1.4,
                            color: '#212b36',
                            mb: 1,
                          }}
                        >
                          {type.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.5,
                            fontSize: '0.75rem',
                            color: '#212b36',
                            flex: 1,
                          }}
                        >
                          {type.description}
                        </Typography>
                      </Box>
                    </Card>
                  ))}
                </Box>
              </Box>

              {formData.promotionType === 'buy-x-get-y-free' && (
                <BuyXGetYFree
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductQuantityChange={handleProductQuantityChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  getProductsSearch={getProductsSearch}
                  setGetProductsSearch={setGetProductsSearch}
                  errors={errors}
                />
              )}

              {formData.promotionType === 'buy-x-get-y-discount' && (
                <BuyXGetYDiscount
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductQuantityChange={handleProductQuantityChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
                  handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  getProductsSearch={getProductsSearch}
                  setGetProductsSearch={setGetProductsSearch}
                  errors={errors}
                />
              )}

              {formData.promotionType === 'buy-x-discount' && (
                <BuyXDiscount
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  errors={errors}
                />
              )}

              {formData.promotionType === 'multi-layer-offer' && (
                <MultiLayerOffer
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductQuantityChange={handleProductQuantityChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  multiLayerStep={multiLayerStep}
                  setMultiLayerStep={setMultiLayerStep}
                  setModalType={setModalType}
                  setCurrentSlabIndex={setCurrentSlabIndex}
                  setModalTitle={setModalTitle}
                  setShowProductModal={setShowProductModal}
                  handleSlabRemoveProduct={handleSlabRemoveProduct}
                  handleSlabProductTypeChange={handleSlabProductTypeChange}
                  handleSlabProductCompulsoryChange={handleSlabProductCompulsoryChange}
                  handleSlabProductQuantityChange={handleSlabProductQuantityChange}
                  handleSlabProductPricingChange={handleSlabProductPricingChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  errors={errors}
                />
              )}

              {formData.promotionType === 'group-offer' && (
                <GroupOffer
                  formData={formData}
                  handleInputChange={handleInputChange}
                  handleOpenProductModal={handleOpenProductModal}
                  handleRemoveProduct={handleRemoveProduct}
                  handleProductCompulsoryChange={handleProductCompulsoryChange}
                  handleProductQuantityChange={handleProductQuantityChange}
                  handleProductTypeChange={handleProductTypeChange}
                  handleProductPricingChange={handleProductPricingChange}
                  handleProductItemDiscountTypeChange={handleProductItemDiscountTypeChange}
                  handleProductItemDiscountValueChange={handleProductItemDiscountValueChange}
                  buyProductsSearch={buyProductsSearch}
                  setBuyProductsSearch={setBuyProductsSearch}
                  getProductsSearch={getProductsSearch}
                  setGetProductsSearch={setGetProductsSearch}
                  errors={errors}
                />
              )}


              <Divider sx={{ borderColor: "rgba(33, 33, 34, 0.82)" }} />
              <Stack direction="row" spacing={2} justifyContent="space-between" flexDirection={{ xs: 'column', md: 'row' }}>
                {formData.promotionType === 'multi-layer-offer' ? (
                  <>
                    <Button
                      variant="outlined"
                      className="button-variant-outlined"
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>
                    <Stack direction="row" spacing={2} flexDirection={{ xs: 'column', md: 'row' }}>
                      {multiLayerStep === 2 && (
                        <Button
                          variant="outlined"
                          className="button-variant-outlined"

                          startIcon={<Iconify icon="eva:arrow-back-fill" />}
                          onClick={() => setMultiLayerStep(1)}
                        >
                          Previous
                        </Button>
                      )}
                      {multiLayerStep === 1 ? (
                        formData.buyProducts && formData.buyProducts.length > 0 && (
                          <Button
                            variant="contained"
                            className="button-variant-contained"
                            endIcon={<Iconify icon="eva:arrow-forward-fill" />}
                            onClick={() => setMultiLayerStep(2)}
                          >
                            Next
                          </Button>
                        )
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleSave}
                          className="button-variant-contained"
                          size="small"
                          disabled={saving}
                          sx={{
                            width: { xs: '100%', md: 'auto' }
                          }}
                          startIcon={
                            saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null
                          }
                        >
                          {saving ? 'Creating...' : 'Create Promotion'}
                        </Button>
                      )}
                    </Stack>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      size="medium"

                      onClick={handleCancel}
                      disabled={saving}
                      className="button-variant-outlined"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="medium"
                      className="button-variant-contained"
                      onClick={handleSave}
                      disabled={saving}

                      startIcon={
                        saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null
                      }
                    >
                      {saving ? 'Creating...' : 'Create Promotion'}
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>
          </LocalizationProvider>
        </Paper>

        <ProductSelectionModal
          open={showProductModal}
          onClose={handleCloseProductModal}
          onConfirm={handleConfirmProducts}
          title={modalTitle}
          selectedProducts={
            modalType === 'getForSlab' && currentSlabIndex !== null
              ? formData.slabs?.[currentSlabIndex]?.getProducts || []
              : formData[modalType === 'buy' ? 'buyProducts' : 'getProducts']
          }
          productTypeFilter={
            formData.promotionType === 'multi-layer-offer' && (modalType === 'buy' || modalType === 'getForSlab')
              ? formData.buyProductType
              : null
          }
          firstBuyProduct={
            formData.promotionType === 'multi-layer-offer' &&
              modalType === 'buy' &&
              formData.buyProducts &&
              formData.buyProducts.length > 0
              ? formData.buyProducts[0]
              : null
          }
          isGetForSlab={modalType === 'getForSlab'}
          alreadyAddedProducts={[]}
          showToast={(message, severity) => {
            if (severity === 'error') toast.error(message);
            else if (severity === 'warning') toast.warning(message);
            else toast.success(message);
          }}
        />

        <Dialog
          open={showZeroPriceWarning}
          onClose={handleCancelZeroPriceWarning}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ bgcolor: '#FFF4E5', color: '#FF9800', borderBottom: '1px solid #FFE0B2' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Iconify icon="eva:alert-triangle-fill" width={24} />
              <Typography
                variant="h6"
                component="span"
                sx={{
                  fontSize: "1.125rem",
                  fontWeight: 600,
                }}
              >
                Zero Price Warning
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Products will be offered for free. Is this correct?
            </Alert>

            <Typography
              variant="body2"
              sx={{
                mb: 2,
                fontWeight: 600,
                fontSize: "0.875rem",
                color: "#212b36",
              }}
            >
              The following products have zero pricing:
            </Typography>

            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              {zeroPriceProducts.map((product, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    bgcolor: '#FFF4E5',
                    borderRadius: 1,
                    border: '1px solid #FFE0B2',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#212B36',
                      fontSize: "0.875rem",
                    }}
                  >
                    {product.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#637381',
                      fontSize: "0.6875rem",
                    }}
                  >
                    Code: {product.code} | Pricing: {product.pricingType} | Location: {product.listType}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color: '#637381',
                fontSize: "0.875rem",
              }}
            >
              If you proceed, these products will be free in this promotion.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleCancelZeroPriceWarning}
              variant="outlined"
              className="button-variant-outlined"
            >
              Cancel & Review
            </Button>
            <Button
              onClick={handleConfirmZeroPriceWarning}
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
              className="button-variant-contained"
            >
              {saving ? 'Creating...' : 'Yes, Proceed'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default AddPromotion;
export { AddPromotion };
